import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  bulkCreateSongs,
  deleteSong,
  enrichSongImportPreview,
  getSongs,
  previewSongImportFile,
  updateSong,
} from "../data/songRequestsApi";

const PRESET_GENRES = [
  "Yleinen",
  ...[
    "Akustinen",
    "Alternative",
    "Balladi",
    "Bluegrass",
    "Blues",
    "Bossa nova",
    "Britpop",
    "Country",
    "Dance",
    "Disco",
    "Elektroninen",
    "Elokuvamusiikki",
    "Eurodance",
    "Folk",
    "Foxtrot",
    "Funk",
    "Gospel",
    "Hard rock",
    "Heavy",
    "Hip hop",
    "Humppa",
    "Indie",
    "Instrumentaali",
    "Iskelmä",
    "Jazz",
    "Jenkka",
    "Joululaulu",
    "Kansanmusiikki",
    "Klassinen",
    "Lastenlaulu",
    "Latin",
    "Musiikkiteatteri",
    "Pop",
    "Pop-rock",
    "Punk",
    "R&B",
    "Rap",
    "Reggae",
    "Rock",
    "Rock'n'roll",
    "Schlager",
    "Soul",
    "Suomipop",
    "Suomirock",
    "Swing",
    "Tango",
    "Valssi",
    "Virsi",
  ].sort((a, b) => a.localeCompare(b, "fi")),
];

const LANGUAGE_OPTIONS = [
  { value: "unknown", label: "Ei valittu" },
  { value: "da", label: "Tanska" },
  { value: "de", label: "Saksa" },
  { value: "en", label: "Englanti" },
  { value: "es", label: "Espanja" },
  { value: "et", label: "Viro" },
  { value: "fi", label: "Suomi" },
  { value: "fr", label: "Ranska" },
  { value: "it", label: "Italia" },
  { value: "no", label: "Norja" },
  { value: "sv", label: "Ruotsi" },
  { value: "other", label: "Muu kieli" },
];

const PRESET_OCCASIONS = [
  "After ski",
  "Asiakastilaisuus",
  "Avajaiset",
  "Baarikeikka",
  "Brunssi",
  "Festivaali",
  "Häät",
  "Hääjuhla",
  "Hääseremonia",
  "Iltamat",
  "Jatkot",
  "Joulujuhla",
  "Joulukeikka",
  "Kesäjuhla",
  "Klubi",
  "Laiva",
  "Lastenjuhlat",
  "Loppuilta",
  "Lounge",
  "Markkinat",
  "Nostatus",
  "Opiskelijabileet",
  "Palvelutalo",
  "Perhejuhlat",
  "Pikkujoulut",
  "Polttarit",
  "Pubi",
  "Pyöreät vuodet",
  "Päivätanssit",
  "Ravintola",
  "Rauhaisa tausta",
  "Rippijuhlat",
  "Risteily",
  "Senioritilaisuus",
  "Sukujuhlat",
  "Syntymäpäivät",
  "Tanssit",
  "Tapahtuma",
  "Terassi",
  "Terassikeikka",
  "Urheilutapahtuma",
  "Valmistujaiset",
  "Vuosijuhla",
  "Yksityisjuhlat",
  "Yhteislaulu",
  "Ylioppilasjuhlat",
  "Yritysjuhlat",
  "Yökerho",
].sort((a, b) => a.localeCompare(b, "fi"));

const CURRENT_DECADE = Math.floor(new Date().getFullYear() / 10) * 10;

const BULK_NO_CHANGE = "__nochange__";
const BULK_CLEAR_VALUE = "__clear__";
const BULK_TAG_NO_CHANGE = "none";
const BULK_TAG_ADD = "add";
const BULK_TAG_REMOVE = "remove";
const BULK_REQUESTABLE_NO_CHANGE = "nochange";
const BULK_REQUESTABLE_TRUE = "requestable";
const BULK_REQUESTABLE_FALSE = "hidden";

const PRESET_DECADES = Array.from(
  { length: (CURRENT_DECADE - 1900) / 10 + 1 },
  (_, index) => `${1900 + index * 10}-luku`
);

function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(90|80|70|60|50)\s*-?\s*luku\b/g, "$1luku")
    .replace(/\b(1990|1980|1970|1960|1950)\s*-?\s*luku\b/g, "$1luku")
    .replace(/[-\s]+/g, "");
}

function emptyEditForm() {
  return {
    title: "",
    artist: "",
    year: "",
    decade: "",
    genre: "Yleinen",
    tempo: "",
    language: "unknown",
    mood: "",
    occasionTags: "",
    notes: "",
    isRequestable: true,
  };
}

function getDecadeFromYear(year) {
  const numericYear = Number(year);

  if (!Number.isFinite(numericYear) || numericYear <= 0) {
    return "";
  }

  return `${Math.floor(numericYear / 10) * 10}-luku`;
}

function getSongDecade(song) {
  return song?.decade || getDecadeFromYear(song?.year);
}

function getSongIsRequestable(song) {
  const rawValue = song?.isRequestable ?? song?.is_requestable;

  if (rawValue === undefined || rawValue === null) {
    return true;
  }

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue === 1;
  }

  return String(rawValue).toLowerCase() !== "false" && String(rawValue) !== "0";
}

function getSongLanguage(song) {
  return song?.language || "unknown";
}

function getSongOccasionTags(song) {
  return song?.occasionTags || song?.occasion_tags || "";
}

function parseOccasionTags(value) {
  return String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function toggleOccasionTag(currentValue, tag) {
  const tags = parseOccasionTags(currentValue);
  const hasTag = tags.includes(tag);

  if (hasTag) {
    return tags.filter((item) => item !== tag).join(", ");
  }

  return [...tags, tag].join(", ");
}

function addOccasionTag(currentValue, tag) {
  const tags = parseOccasionTags(currentValue);

  if (tags.includes(tag)) {
    return tags.join(", ");
  }

  return [...tags, tag].join(", ");
}

function removeOccasionTag(currentValue, tag) {
  return parseOccasionTags(currentValue)
    .filter((item) => item !== tag)
    .join(", ");
}

function shouldShowLanguagePill(value) {
  return Boolean(value) && value !== "unknown";
}

function formatGenreLabel(value) {
  const genre = String(value || "").trim();

  if (!genre) {
    return "Yleinen";
  }

  if (normalize(genre) === "yleinen") {
    return "Yleinen";
  }

  return genre.charAt(0).toLocaleUpperCase("fi-FI") + genre.slice(1);
}

function formatOccasionTagLabel(value) {
  const tag = String(value || "").trim();

  if (!tag) {
    return "";
  }

  return tag.charAt(0).toLocaleUpperCase("fi-FI") + tag.slice(1);
}

function getOccasionTagClass(tag) {
  const normalizedTag = normalize(tag);

  if (normalizedTag.includes("haa")) {
    return [
      "border-rose-300 bg-rose-50 text-rose-700",
      "theme-dark:border-rose-400/35",
      "theme-dark:bg-rose-500/10",
      "theme-dark:text-rose-100",
    ].join(" ");
  }

  if (
    normalizedTag.includes("pubi") ||
    normalizedTag.includes("ravintola") ||
    normalizedTag.includes("baari") ||
    normalizedTag.includes("klubi") ||
    normalizedTag.includes("yokerho")
  ) {
    return [
      "border-amber-300 bg-amber-50 text-amber-800",
      "theme-dark:border-amber-300/30",
      "theme-dark:bg-amber-400/10",
      "theme-dark:text-amber-100",
    ].join(" ");
  }

  if (
    normalizedTag.includes("laiva") ||
    normalizedTag.includes("risteily")
  ) {
    return [
      "border-sky-300 bg-sky-50 text-sky-700",
      "theme-dark:border-sky-300/30",
      "theme-dark:bg-sky-400/10",
      "theme-dark:text-sky-100",
    ].join(" ");
  }

  if (
    normalizedTag.includes("joulu") ||
    normalizedTag.includes("pikkujoulu")
  ) {
    return [
      "border-red-300 bg-red-50 text-red-700",
      "theme-dark:border-red-300/30",
      "theme-dark:bg-red-400/10",
      "theme-dark:text-red-100",
    ].join(" ");
  }

  if (
    normalizedTag.includes("syntymapaiva") ||
    normalizedTag.includes("perhejuhla") ||
    normalizedTag.includes("yksityisjuhla") ||
    normalizedTag.includes("valmistujaiset") ||
    normalizedTag.includes("ylioppilas")
  ) {
    return [
      "border-violet-300 bg-violet-50 text-violet-700",
      "theme-dark:border-violet-300/30",
      "theme-dark:bg-violet-400/10",
      "theme-dark:text-violet-100",
    ].join(" ");
  }

  if (
    normalizedTag.includes("palvelutalo") ||
    normalizedTag.includes("seniori") ||
    normalizedTag.includes("rauhaisa")
  ) {
    return [
      "border-emerald-300 bg-emerald-50 text-emerald-700",
      "theme-dark:border-emerald-300/30",
      "theme-dark:bg-emerald-400/10",
      "theme-dark:text-emerald-100",
    ].join(" ");
  }

  return [
    "border-red-300 bg-red-50 text-red-700",
    "theme-dark:border-[#ef4444]/25",
    "theme-dark:bg-[#ef4444]/10",
    "theme-dark:text-[#fecaca]",
  ].join(" ");
}

function getSongNotes(song) {
  return String(song?.notes || "").trim();
}

function hasChordProNotes(song) {
  return getSongNotes(song).includes("SANAT JA SOINNUT / CHORDPRO");
}

function isChordProFile(file) {
  const fileName = String(file?.name || "").toLowerCase();

  return (
    fileName.endsWith(".pro") ||
    fileName.endsWith(".cho") ||
    fileName.endsWith(".chordpro")
  );
}

function getChordProDirective(content, directiveNames) {
  const names = Array.isArray(directiveNames) ? directiveNames : [directiveNames];

  for (const name of names) {
    const pattern = new RegExp(`^\\\\s*\\\\{${name}\\\\s*:\\\\s*([^}]+)\\\\}`, "im");
    const match = String(content || "").match(pattern);

    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return "";
}

function inferSongFromFileName(fileName) {
  const cleanName = String(fileName || "")
    .replace(/\.(pro|cho|chordpro)$/i, "")
    .trim();

  const parts = cleanName
    .split(/\s+-\s+|\s+–\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return {
      title: parts[0],
      artist: parts.slice(1).join(" - "),
    };
  }

  return {
    title: cleanName,
    artist: "",
  };
}

function parseChordProContent(content, fileName) {
  const inferred = inferSongFromFileName(fileName);

  const title =
    getChordProDirective(content, ["title", "t"]) ||
    inferred.title;

  const artist =
    getChordProDirective(content, ["artist", "subtitle", "st"]) ||
    inferred.artist;

  const key = getChordProDirective(content, ["key", "k"]);
  const duration = getChordProDirective(content, ["time", "duration"]);

  return {
    sourceType: "chordpro",
    title,
    artist,
    key,
    duration,
    raw_header: fileName,
    chordProContent: String(content || "").trim(),
  };
}

function buildChordProNotes(preview, translate = (_key, fallback) => fallback) {
  return [
    "SANAT JA SOINNUT / CHORDPRO",
    "",
    preview?.title ? `${translate("import.songLabel", "Biisi:")} ${preview.title}` : "",
    preview?.artist ? `${translate("import.artistLabel", "Artisti:")} ${preview.artist}` : "",
    preview?.key ? `${translate("import.keyLabel", "Sävellaji:")} ${preview.key}` : "",
    preview?.duration ? `${translate("import.durationLabel", "Kesto:")} ${preview.duration}` : "",
    "",
    "----",
    "",
    preview?.chordProContent || "",
  ]
    .filter((line) => line !== "")
    .join("\n");
}

export default function SongLibraryAdmin({ performerSlug = null, features = {} }) {
  const { t, i18n } = useTranslation("admin");
  const editorRef = useRef(null);
  const importFileInputRef = useRef(null);

  const adminLocale =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en-GB"
      : "fi-FI";

  function tr(key, defaultValue, values = {}) {
    return t(`songLibraryAdmin.${key}`, {
      defaultValue,
      ...values,
    });
  }

  function createLocalizedRequestError(data, fallbackMessage) {
    const error = new Error(fallbackMessage);
    error.code = data?.code || "";
    error.payload = data || {};
    error.backendMessage = String(data?.error || "").trim();
    return error;
  }

  function getLocalizedErrorMessage(error, fallbackMessage) {
    const message = String(error?.message || "").trim();
    return error?.backendMessage !== undefined && message ? message : fallbackMessage;
  }

  function getLocalizedLanguageLabel(value, useMissingLabel = false) {
    const normalizedValue = String(value || "unknown").trim();
    const option =
      LANGUAGE_OPTIONS.find((item) => item.value === normalizedValue) ||
      LANGUAGE_OPTIONS.find((item) => item.value === "unknown");

    if (normalizedValue === "unknown" && useMissingLabel) {
      return tr("languages.missing", "Kieli puuttuu");
    }

    return tr(
      `languages.${normalizedValue}`,
      option?.label || "Ei valittu"
    );
  }

  function getLocalizedTempoLabel(value) {
    const tempo = String(value || "").trim();

    if (tempo === "calm") {
      return tr("tempo.calm", "Rauhallinen");
    }

    if (tempo === "medium") {
      return tr("tempo.medium", "Keskitempo");
    }

    if (tempo === "upbeat") {
      return tr("tempo.upbeat", "Menevä");
    }

    return "";
  }

  const genreTranslationKeys = {
    Yleinen: "general",
    Akustinen: "acoustic",
    Balladi: "ballad",
    Elektroninen: "electronic",
    Elokuvamusiikki: "filmMusic",
    Instrumentaali: "instrumental",
    Iskelmä: "finnishSchlager",
    Joululaulu: "christmasSong",
    Kansanmusiikki: "folkMusic",
    Klassinen: "classical",
    Lastenlaulu: "childrenSong",
    Musiikkiteatteri: "musicalTheatre",
    Suomipop: "finnishPop",
    Suomirock: "finnishRock",
    Valssi: "waltz",
    Virsi: "hymn",
  };

  function getLocalizedGenreLabel(value) {
    const label = formatGenreLabel(value);
    const key = genreTranslationKeys[label];

    return key
      ? tr(`genres.${key}`, label)
      : label;
  }

  const occasionTranslationKeys = {
    "After ski": "afterSki",
    Asiakastilaisuus: "customerEvent",
    Avajaiset: "opening",
    Baarikeikka: "barGig",
    Brunssi: "brunch",
    Festivaali: "festival",
    Häät: "wedding",
    Hääjuhla: "weddingReception",
    Hääseremonia: "weddingCeremony",
    Iltamat: "socialEvening",
    Jatkot: "afterparty",
    Joulujuhla: "christmasCelebration",
    Joulukeikka: "christmasGig",
    Kesäjuhla: "summerParty",
    Klubi: "club",
    Laiva: "ship",
    Lastenjuhlat: "childrenParty",
    Loppuilta: "lateEvening",
    Lounge: "lounge",
    Markkinat: "market",
    Nostatus: "partyLift",
    Opiskelijabileet: "studentParty",
    Palvelutalo: "careHome",
    Perhejuhlat: "familyCelebration",
    Pikkujoulut: "christmasParty",
    Polttarit: "stagHenParty",
    Pubi: "pub",
    "Pyöreät vuodet": "milestoneBirthday",
    Päivätanssit: "afternoonDance",
    Ravintola: "restaurant",
    "Rauhaisa tausta": "calmBackground",
    Rippijuhlat: "confirmationParty",
    Risteily: "cruise",
    Senioritilaisuus: "seniorEvent",
    Sukujuhlat: "familyGathering",
    Syntymäpäivät: "birthdayParty",
    Tanssit: "dance",
    Tapahtuma: "event",
    Terassi: "terrace",
    Terassikeikka: "terraceGig",
    Urheilutapahtuma: "sportsEvent",
    Valmistujaiset: "graduationParty",
    Vuosijuhla: "anniversary",
    Yksityisjuhlat: "privateParty",
    Yhteislaulu: "singalong",
    Ylioppilasjuhlat: "graduationCelebration",
    Yritysjuhlat: "corporateParty",
    Yökerho: "nightclub",
  };

  function getLocalizedOccasionTagLabel(value) {
    const label = formatOccasionTagLabel(value);
    const key = occasionTranslationKeys[label];

    return key
      ? tr(`occasions.${key}`, label)
      : label;
  }

  function getLocalizedDecadeLabel(value) {
    const text = String(value || "").trim();
    const year = text.match(/\d{4}/)?.[0];

    if (!year) {
      return text;
    }

    return tr("decadeLabel", "{{year}}-luku", {
      year,
    });
  }

  const localizedOccasionOptions = [...PRESET_OCCASIONS].sort((a, b) =>
    getLocalizedOccasionTagLabel(a).localeCompare(
      getLocalizedOccasionTagLabel(b),
      adminLocale,
      { sensitivity: "base" }
    )
  );

  const canUseSongImport = features?.songImportEnabled === true;

  // Sanat ja soinnut kuuluvat samaan Plus-ominaisuusrajaan kuin ChordPro-tuonti.
  const canUseChordLyrics = canUseSongImport;
  const [songs, setSongs] = useState([]);
  const [query, setQuery] = useState("");

  const [artistFilter, setArtistFilter] = useState("all");
  const [genreFilter, setGenreFilter] = useState("all");
  const [decadeFilter, setDecadeFilter] = useState("all");
  const [tempoFilter, setTempoFilter] = useState("all");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [requestableFilter, setRequestableFilter] = useState("all");

  const [bulkEditLanguage, setBulkEditLanguage] = useState(BULK_NO_CHANGE);
  const [bulkEditGenre, setBulkEditGenre] = useState(BULK_NO_CHANGE);
  const [bulkEditDecade, setBulkEditDecade] = useState(BULK_NO_CHANGE);
  const [bulkEditTempo, setBulkEditTempo] = useState(BULK_NO_CHANGE);
  const [bulkEditTagAction, setBulkEditTagAction] = useState(BULK_TAG_NO_CHANGE);
  const [bulkEditRequestableMode, setBulkEditRequestableMode] = useState(
    BULK_REQUESTABLE_NO_CHANGE
  );
  const [statusMessage, setStatusMessage] = useState("");
  const [statusMessageKey, setStatusMessageKey] = useState(0);
  const [sortDirection, setSortDirection] = useState("asc");
  const [bulkGenre, setBulkGenre] = useState("Yleinen");
  const [customBulkGenre, setCustomBulkGenre] = useState("");
  const [bulkLanguage, setBulkLanguage] = useState("unknown");
  const [bulkDecade, setBulkDecade] = useState("");
  const [bulkTempo, setBulkTempo] = useState("");
  const [bulkYear, setBulkYear] = useState("");
  const [bulkSongsText, setBulkSongsText] = useState("");
  const [bulkOccasionTag, setBulkOccasionTag] = useState("Häät");
  const [isBulkOccasionMenuOpen, setIsBulkOccasionMenuOpen] = useState(false);
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkProgressMessage, setBulkProgressMessage] = useState("");
  const [visibleSongCount, setVisibleSongCount] = useState(80);
  const [importFile, setImportFile] = useState(null);
  const [importFiles, setImportFiles] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviews, setImportPreviews] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importEnrichLoading, setImportEnrichLoading] = useState(false);
  const [importEnrichSuggestions, setImportEnrichSuggestions] = useState([]);
  const [importInputResetKey, setImportInputResetKey] = useState(0);
  const [importUrl, setImportUrl] = useState("");
  const [importUrlLoading, setImportUrlLoading] = useState(false);
  const [isCloudImportInfoOpen, setIsCloudImportInfoOpen] = useState(false);
  const [isBulkInputOpen, setIsBulkInputOpen] = useState(false);
  const [songLimitModal, setSongLimitModal] = useState(null);

  const [editingSongId, setEditingSongId] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyEditForm());
  const [deleteConfirmSong, setDeleteConfirmSong] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);
  const [tagConfirmSelection, setTagConfirmSelection] = useState(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichSuggestions, setEnrichSuggestions] = useState([]);

  const isEditing = editingSongId !== null;

  const effectivePerformerSlug =
  performerSlug ||
  (() => {
    try {
      return JSON.parse(
        window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
      )?.slug || null;
    } catch {
      return null;
    }
  })();

  async function refreshSongs() {
    try {
      const data = await getSongs(effectivePerformerSlug);
      setSongs(data.items || []);
    } catch (error) {
      console.error("Biisikirjaston haku epäonnistui:", error);
      showStatus(tr("messages.libraryLoadFailed", "Biisikirjaston haku epäonnistui."));
    }
  }

  useEffect(() => {
    refreshSongs();
  }, [effectivePerformerSlug]);

  useEffect(() => {
    if (!statusMessage) {
      return;
    }

    const timer = window.setTimeout(() => {
      setStatusMessage("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [statusMessage, statusMessageKey]);

  function showStatus(message) {
    setStatusMessage(message);
    setStatusMessageKey((current) => current + 1);
  }

  const languageCounts = useMemo(() => {
  return songs.reduce(
    (counts, song) => {
      const language = getSongLanguage(song);

      counts.all += 1;
      counts[language] = (counts[language] || 0) + 1;

      return counts;
    },
    { all: 0 }
  );
}, [songs]);

const filteredSongs = useMemo(() => {
  const search = normalize(query);

  const searchFilteredSongs = search
    ? songs.filter((song) => {
        const haystack = normalize(
          [
            song.title,
            song.artist,
            song.year,
            song.decade,
            song.genre,
            song.tempo,
            song.language,
            getSongOccasionTags(song),
          ].join(" ")
        );

        return haystack.includes(search);
      })
    : songs;

  const artistFilteredSongs =
    artistFilter === "all"
      ? searchFilteredSongs
      : searchFilteredSongs.filter(
          (song) => normalize(song.artist) === normalize(artistFilter)
        );

  const genreFilteredSongs =
    genreFilter === "all"
      ? artistFilteredSongs
      : artistFilteredSongs.filter(
          (song) => normalize(song.genre) === normalize(genreFilter)
        );

  const decadeFilteredSongs =
    decadeFilter === "all"
      ? genreFilteredSongs
      : genreFilteredSongs.filter(
          (song) => getSongDecade(song) === decadeFilter
        );

  const tempoFilteredSongs =
    tempoFilter === "all"
      ? decadeFilteredSongs
      : decadeFilteredSongs.filter(
          (song) => String(song.tempo || "").trim() === tempoFilter
        );

  const languageFilteredSongs =
    languageFilter === "all"
      ? tempoFilteredSongs
      : tempoFilteredSongs.filter(
          (song) => getSongLanguage(song) === languageFilter
        );

  const requestableFilteredSongs =
    requestableFilter === "requestable"
      ? languageFilteredSongs.filter((song) => getSongIsRequestable(song))
      : requestableFilter === "hidden"
        ? languageFilteredSongs.filter((song) => !getSongIsRequestable(song))
        : languageFilteredSongs;

  return [...requestableFilteredSongs].sort((a, b) => {
    const titleA = String(a.title || "").trim();
    const titleB = String(b.title || "").trim();

    const comparison = titleA.localeCompare(titleB, adminLocale, {
      sensitivity: "base",
      numeric: true,
    });

    return sortDirection === "asc" ? comparison : -comparison;
  });
}, [
  songs,
  query,
  artistFilter,
  genreFilter,
  decadeFilter,
  tempoFilter,
  languageFilter,
  requestableFilter,
  sortDirection,
  adminLocale,
]);

const requestableSongCount = useMemo(
  () => songs.filter((song) => getSongIsRequestable(song)).length,
  [songs]
);

const visibleSongs = useMemo(() => {
  return filteredSongs.slice(0, visibleSongCount);
}, [filteredSongs, visibleSongCount]);

useEffect(() => {
  setVisibleSongCount(80);
}, [
  query,
  artistFilter,
  genreFilter,
  decadeFilter,
  tempoFilter,
  languageFilter,
  requestableFilter,
  sortDirection,
]);

  const hasBulkSelectionFilter =
  Boolean(String(query || "").trim()) ||
  artistFilter !== "all" ||
  genreFilter !== "all" ||
  decadeFilter !== "all" ||
  tempoFilter !== "all" ||
  languageFilter !== "all" ||
  requestableFilter !== "all";

  const selectedSongIdSet = useMemo(() => {
    return new Set(selectedSongIds.map((id) => String(id)));
  }, [selectedSongIds]);

  const selectedFilteredSongIds = useMemo(() => {
  if (!hasBulkSelectionFilter) {
    return [];
  }

  return filteredSongs.map((song) => String(song.id));
  }, [filteredSongs, hasBulkSelectionFilter]);

  const allSongIds = useMemo(() => {
    return songs.map((song) => String(song.id));
  }, [songs]);

  const areAllSongsSelected =
    allSongIds.length > 0 &&
    allSongIds.every((id) => selectedSongIdSet.has(id));

  const areAllFilteredSongsSelected =
    selectedFilteredSongIds.length > 0 &&
    selectedFilteredSongIds.every((id) => selectedSongIdSet.has(id));

  const artistOptions = useMemo(() => {
    const optionMap = new Map();

    [
      ...songs.map((song) => song.artist).filter(Boolean),
      editForm.artist,
    ]
      .filter(Boolean)
      .forEach((artist) => {
        const label = String(artist || "").trim();
        const key = normalize(label);

        if (!label || !key) {
          return;
        }

        const currentLabel = optionMap.get(key);

        if (!currentLabel) {
          optionMap.set(key, label);
          return;
        }

        /*
         * Sama artisti voi olla tallennettu eri kirjainkoolla
         * (esim. "Don huonot" / "Don Huonot").
         * Suodattimessa näytetään vain yksi vaihtoehto.
         */
        const getDisplayScore = (value) =>
          String(value)
            .split(/\s+/)
            .filter(Boolean)
            .reduce(
              (score, word) =>
                score + (/^[A-ZÅÄÖ]/.test(word) ? 1 : 0),
              0
            );

        if (getDisplayScore(label) > getDisplayScore(currentLabel)) {
          optionMap.set(key, label);
        }
      });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.localeCompare(b, adminLocale, {
        sensitivity: "base",
      })
    );
  }, [songs, editForm.artist, adminLocale]);

const genreOptions = useMemo(() => {
  const optionMap = new Map();

  [
    ...PRESET_GENRES,
    ...songs.map((song) => song.genre).filter(Boolean),
    editForm.genre,
    bulkGenre,
  ]
    .filter(Boolean)
    .forEach((genre) => {
      const label = formatGenreLabel(genre);
      const key = normalize(label);

      if (!optionMap.has(key)) {
        optionMap.set(key, label);
      }
    });

  return Array.from(optionMap.values()).sort((a, b) =>
    a.localeCompare(b, adminLocale, {
      sensitivity: "base",
    })
  );
}, [songs, editForm.genre, bulkGenre, adminLocale]);

  const decadeOptions = useMemo(() => {
    return [
      ...new Set([
        ...PRESET_DECADES,
        ...songs.map((song) => getSongDecade(song)).filter(Boolean),
        editForm.decade,
        bulkDecade,
      ].filter(Boolean)),
    ].sort((a, b) => {
      const numberA = Number(String(a).match(/\\d{4}/)?.[0] || 0);
      const numberB = Number(String(b).match(/\\d{4}/)?.[0] || 0);

      return numberA - numberB;
    });
  }, [songs, editForm.decade, bulkDecade]);

  const yearOptions = useMemo(() => {
    return [
      ...new Set([
        ...songs.map((song) => song.year).filter(Boolean),
        editForm.year,
        bulkYear,
      ].filter(Boolean)),
    ].sort((a, b) => Number(a) - Number(b));
  }, [songs, editForm.year, bulkYear]);


  function startEdit(song) {
    setEnrichSuggestions([]);
    setEditingSongId(song.id);
    setEditForm({
      title: song.title || "",
      artist: song.artist || "",
      year: song.year || "",
      decade: getSongDecade(song),
      genre: song.genre || "Yleinen",
      tempo: song.tempo || "",
      language: getSongLanguage(song),
      mood: song.mood || "",
      occasionTags: getSongOccasionTags(song),
      notes: song.notes || "",
      isRequestable: getSongIsRequestable(song),
    });

    window.setTimeout(() => {
      editorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  }

  function cancelEdit() {
    setEditingSongId(null);
    setEnrichSuggestions([]);
    setEditForm(emptyEditForm());
  }

async function handleEnrichCurrentSong() {
  if (!editingSongId || isEnriching) {
    return;
  }

  const title = String(editForm.title || "").trim();
  const artist = String(editForm.artist || "").trim();

  if (!title) {
    showStatus(tr("messages.enterSongTitle", "Anna ensin biisin nimi."));
    return;
  }

  setIsEnriching(true);
  setEnrichSuggestions([]);

  try {
    const response = await fetchWithCredentials(
      `${getSongLibraryApiBaseUrl()}/songs/import-preview-enrich`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          title,
          artist,
        }),
      }
    );

    const result = await response.json().catch(() => null);

    if (!response.ok || !result?.ok) {
      throw createLocalizedRequestError(
        result,
        tr("messages.enrichFailedHttp", "Biisin tietojen haku epäonnistui. HTTP {{status}}", {
          status: response.status,
        })
      );
    }

    const suggestions = [
      result.suggestion,
      ...(Array.isArray(result.matches) ? result.matches : []),
    ]
      .filter(Boolean)
      .filter((item, index, array) => {
        const key = `${item.title || ""}|${item.artist || ""}|${item.year || ""}`;

        return (
          array.findIndex(
            (other) =>
              `${other.title || ""}|${other.artist || ""}|${other.year || ""}` === key
          ) === index
        );
      });

    if (!suggestions.length) {
      showStatus(tr("messages.noWebSuggestions", "Verkosta ei löytynyt ehdotuksia."));
      return;
    }

    setEnrichSuggestions(suggestions);

    const firstSuggestion = suggestions[0];

    setEditForm((current) => ({
      ...current,
      title: firstSuggestion.title || current.title,
      artist: firstSuggestion.artist || current.artist,
      year: firstSuggestion.year ? String(firstSuggestion.year) : current.year,
      decade: firstSuggestion.year
        ? getDecadeFromYear(firstSuggestion.year)
        : current.decade,
      genre:
        firstSuggestion.genre &&
        (!current.genre || normalize(current.genre) === "yleinen")
          ? firstSuggestion.genre
          : current.genre,
    }));

    showStatus(tr("messages.suggestionsLoadedBestEdit", "Ehdotukset haettu ja paras osuma valittu. Tarkista ja tallenna muutokset."));
  } catch (error) {
    console.error("Biisin tietojen haku epäonnistui:", error);
    showStatus(getLocalizedErrorMessage(error, tr("messages.enrichFailed", "Biisin tietojen haku epäonnistui.")));
  } finally {
    setIsEnriching(false);
  }
}

  function applyEnrichSuggestion(suggestion) {
    setEditForm((current) => ({
      ...current,
      title: suggestion.title || current.title,
      artist: suggestion.artist || current.artist,
      year: suggestion.year ? String(suggestion.year) : current.year,
      decade:
        suggestion.year && !current.decade
          ? getDecadeFromYear(suggestion.year)
          : current.decade,
      genre:
        suggestion.genre &&
        (!current.genre || normalize(current.genre) === "yleinen")
          ? suggestion.genre
          : current.genre,
    }));

    showStatus(tr("messages.suggestionSelectedEdit", "Ehdotus valittu. Tarkista ja tallenna muutokset."));
  }

async function saveEdit() {
  if (!editingSongId) {
    return;
  }

  if (!editForm.title.trim()) {
    showStatus(tr("messages.songTitleRequired", "Biisin nimi ei voi olla tyhjä."));
    return;
  }

  try {
    const normalizedOccasionTags = Array.isArray(editForm.occasionTags)
      ? editForm.occasionTags
      : String(editForm.occasionTags || "")
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean);

    const payload = {
      title: String(editForm.title || "").trim(),
      artist: String(editForm.artist || "").trim(),
      year: editForm.year ? Number(editForm.year) : null,
      decade: String(editForm.decade || "").trim(),
      genre: String(editForm.genre || "").trim(),
      tempo: String(editForm.tempo || "").trim(),
      language: editForm.language || "unknown",
      mood: String(editForm.mood || "").trim(),
      occasion_tags: normalizedOccasionTags,
      occasionTags: normalizedOccasionTags,
      notes: String(editForm.notes || "").trim(),
      isRequestable: editForm.isRequestable,
      is_requestable: editForm.isRequestable,
    };

    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    const url = slug
      ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(editingSongId)}`
      : `${apiBaseUrl}/songs/${encodeURIComponent(editingSongId)}`;

    const response = await fetchWithCredentials(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      throw createLocalizedRequestError(result, tr("messages.songUpdateFailed", "Biisin päivitys epäonnistui."));
    }

    showStatus(tr("messages.songUpdated", "Biisi päivitetty."));
    cancelEdit();
    await refreshSongs();
  } catch (error) {
    console.error("Biisin päivitys epäonnistui:", error);

    showStatus(
      getLocalizedErrorMessage(error, tr("messages.songUpdateFailed", "Biisin päivitys epäonnistui."))
    );
  }
}

function getSongLibraryApiBaseUrl() {
  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  return String(apiBaseUrl).replace(/\/$/, "");
}

function isAllowedCloudImportUrl(value) {
  try {
    const parsedUrl = new URL(String(value || "").trim());
    const hostname = parsedUrl.hostname.toLowerCase();

    return (
      hostname === "drive.google.com" ||
      hostname === "docs.google.com"
    );
  } catch {
    return false;
  }
}

async function previewImportUrl() {
  const cleanUrl = String(importUrl || "").trim();

  if (!cleanUrl) {
    showStatus(tr("messages.pasteDriveLink", "Liitä ensin Google Drive -jakolinkki."));
    return;
  }

  if (!isAllowedCloudImportUrl(cleanUrl)) {
    showStatus(tr("messages.invalidDriveLink", "Linkin pitää olla Google Drive -jakolinkki."));
    return;
  }

  const slug = String(effectivePerformerSlug || performerSlug || "").trim();

  if (!slug) {
    showStatus(tr("messages.performerMissing", "Esiintyjän tunniste puuttuu. Kirjaudu ulos ja takaisin sisään."));
    return;
  }

  setImportUrlLoading(true);
  setImportPreview(null);
  setImportPreviews([]);
  setImportFile(null);
  setImportFiles([]);
  setImportInputResetKey((current) => current + 1);

  try {
    const response = await fetchWithCredentials(
      `${getSongLibraryApiBaseUrl()}/admin/performers/${encodeURIComponent(
        slug
      )}/songs/import-url/preview`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          url: cleanUrl,
        }),
      }
    );

    const result = await response.json().catch(() => null);

    if (!response.ok || !result?.ok) {
      throw createLocalizedRequestError(result, tr("messages.linkImportFailed", "Linkistä tuonti epäonnistui."));
    }

    const previews = Array.isArray(result.previews)
      ? result.previews
      : Array.isArray(result.items)
        ? result.items
        : [];

    if (previews.length > 1) {
      setImportPreviews(previews);
      setImportPreview(previews[0]);
      showStatus(
        tr("messages.linkReadMultiple", "Linkistä luettu {{count}} biisiä. Tarkista tiedot ennen tallennusta.", {
          count: previews.length,
        })
      );
      return;
    }

    const preview = result.preview || result.item || previews[0];

    if (!preview?.title) {
      showStatus(tr("messages.linkNoRecognizableSong", "Linkistä ei löytynyt tunnistettavaa biisiä."));
      return;
    }

    setImportPreview(preview);
    showStatus(tr("messages.linkRead", "Linkki luettu. Tarkista tiedot ennen tallennusta."));
  } catch (error) {
    console.error("Linkistä tuonti epäonnistui:", error);
    showStatus(getLocalizedErrorMessage(error, tr("messages.linkImportFailed", "Linkistä tuonti epäonnistui.")));
  } finally {
    setImportUrlLoading(false);
  }
}

async function previewImportFile() {
  const selectedFiles = importFiles.length
    ? importFiles
    : importFile
      ? [importFile]
      : [];

  if (!selectedFiles.length) {
    showStatus(tr("messages.selectImportFile", "Valitse ensin PDF-, DOCX- tai PRO-tiedosto."));
    return;
  }

  setImportLoading(true);
  setImportPreview(null);
  setImportPreviews([]);

  try {
    const chordProFiles = selectedFiles.filter(isChordProFile);

    if (chordProFiles.length) {
      const previews = await Promise.all(
        chordProFiles.map(async (file) => {
          const content = await file.text();
          return parseChordProContent(content, file.name);
        })
      );

      const validPreviews = previews.filter((preview) => preview.title);

      if (!validPreviews.length) {
        showStatus(tr("messages.proNoSongTitles", "PRO-tiedostoista ei löytynyt biisien nimiä."));
        return;
      }

      setImportPreviews(validPreviews);
      setImportPreview(validPreviews[0]);
      showStatus(
        tr("messages.proReadMultiple", "Luettu {{count}} SongBook/ChordPro-biisiä. Tarkista tiedot ennen tallennusta.", {
          count: validPreviews.length,
        })
      );
      return;
    }

    const result = await previewSongImportFile(selectedFiles[0], effectivePerformerSlug);
    setImportPreview(result);
    showStatus(tr("messages.fileRead", "Tiedosto luettu. Tarkista tiedot ennen tallennusta."));
  } catch (error) {
    console.error("Tiedoston esikatselu epäonnistui:", error);
    showStatus(getLocalizedErrorMessage(error, tr("messages.filePreviewFailed", "Tiedoston esikatselu epäonnistui.")));
  } finally {
    setImportLoading(false);
  }
}

async function saveChordProContentToImportedSong(
  preview,
  createdSongs = []
) {
   if (preview?.sourceType !== "chordpro" || !preview?.chordProContent) {
     return false;
   }

  /*
   * Bulk-luonti palauttaa juuri luodut biisit ID-tietoineen.
   * Käytetään niitä ensisijaisesti, jotta ChordPro-sisällön tallennus
   * ei riipu siitä, ehtiikö uusi biisi jo seuraavaan GET-hakuun.
   */
  const directlyCreatedSongs = Array.isArray(createdSongs)
    ? createdSongs
    : [];

  const data = await getSongs(effectivePerformerSlug);
  const fetchedSongs = Array.isArray(data.items) ? data.items : [];

  const currentSongs = [
    ...directlyCreatedSongs,
    ...fetchedSongs,
  ].filter(

    (song, index, allSongs) =>
      allSongs.findIndex(
        (candidate) => String(candidate.id) === String(song.id)
      ) === index
  );

  setSongs(currentSongs);

  const importedSong =
    currentSongs.find((song) => {
      const sameTitle = normalize(song.title) === normalize(preview.title);
      const sameArtist =
        !preview.artist || normalize(song.artist) === normalize(preview.artist);

      return sameTitle && sameArtist;
    }) ||
    currentSongs.find((song) => normalize(song.title) === normalize(preview.title));

  if (!importedSong?.id) {
    return false;
  }

  const rawOccasionTags = getSongOccasionTags(importedSong);
  const normalizedOccasionTags = Array.isArray(rawOccasionTags)
    ? rawOccasionTags
    : String(rawOccasionTags || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

  const payload = {
    title: String(importedSong.title || preview.title || "").trim(),
    artist: String(importedSong.artist || preview.artist || "").trim(),
    year: importedSong.year ? Number(importedSong.year) : null,
    decade: getSongDecade(importedSong),
    genre: String(importedSong.genre || bulkGenre || "Yleinen").trim(),
    tempo: String(importedSong.tempo || bulkTempo || "").trim(),
    language: getSongLanguage(importedSong),
    mood: String(importedSong.mood || "").trim(),
    occasion_tags: normalizedOccasionTags,
    occasionTags: normalizedOccasionTags,
    notes: buildChordProNotes(preview, tr),
    isRequestable: getSongIsRequestable(importedSong),
    is_requestable: getSongIsRequestable(importedSong),
  };

  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  const slug = String(effectivePerformerSlug || performerSlug || "").trim();

  const url = slug
    ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(importedSong.id)}`
    : `${apiBaseUrl}/songs/${encodeURIComponent(importedSong.id)}`;

  const response = await fetchWithCredentials(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok) {
    throw createLocalizedRequestError(result, tr("messages.chordProSaveFailed", "ChordPro-sisällön tallennus epäonnistui."));
  }

  await refreshSongs();

  return true;
}

async function saveChordProContentsToImportedSongs(
  previews,
  createdSongs = []
) {
  const chordProPreviews = previews.filter(
    (preview) => preview?.sourceType === "chordpro" && preview?.chordProContent
  );

  if (!chordProPreviews.length) {
    return 0;
  }

  const directlyCreatedSongs = Array.isArray(createdSongs)
    ? createdSongs
    : [];

  const data = await getSongs(effectivePerformerSlug);
  const fetchedSongs = Array.isArray(data.items) ? data.items : [];

  const currentSongs = [
    ...directlyCreatedSongs,
    ...fetchedSongs,
  ].filter(
    (song, index, allSongs) =>
      allSongs.findIndex(
        (candidate) => String(candidate.id) === String(song.id)
      ) === index
  );

  setSongs(currentSongs);

  const apiBaseUrl =
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api");

  const slug = String(effectivePerformerSlug || performerSlug || "").trim();

  let savedCount = 0;

  for (const preview of chordProPreviews) {
    const importedSong =
      currentSongs.find((song) => {
        const sameTitle = normalize(song.title) === normalize(preview.title);
        const sameArtist =
          !preview.artist || normalize(song.artist) === normalize(preview.artist);

        return sameTitle && sameArtist;
      }) ||
      currentSongs.find((song) => normalize(song.title) === normalize(preview.title));

    if (!importedSong?.id) {
      continue;
    }

    const rawOccasionTags = getSongOccasionTags(importedSong);
    const normalizedOccasionTags = Array.isArray(rawOccasionTags)
      ? rawOccasionTags
      : String(rawOccasionTags || "")
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean);

    const payload = {
      title: String(importedSong.title || preview.title || "").trim(),
      artist: String(importedSong.artist || preview.artist || "").trim(),
      year: importedSong.year ? Number(importedSong.year) : null,
      decade: getSongDecade(importedSong),
      genre: String(importedSong.genre || bulkGenre || "Yleinen").trim(),
      tempo: String(importedSong.tempo || bulkTempo || "").trim(),
      language: getSongLanguage(importedSong),
      mood: String(importedSong.mood || "").trim(),
      occasion_tags: normalizedOccasionTags,
      occasionTags: normalizedOccasionTags,
      notes: buildChordProNotes(preview, tr),
      isRequestable: getSongIsRequestable(importedSong),
      is_requestable: getSongIsRequestable(importedSong),
    };

    const url = slug
      ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(importedSong.id)}`
      : `${apiBaseUrl}/songs/${encodeURIComponent(importedSong.id)}`;

    const response = await fetchWithCredentials(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      savedCount += 1;
    }
  }

  await refreshSongs();

  return savedCount;
}

  async function handleImportPreviewEnrich() {
    if (!importPreview?.title) {
      showStatus(tr("messages.readFileFirst", "Lue tiedosto ensin, jotta biisin nimi löytyy."));
      return;
    }

    setImportEnrichLoading(true);
    setImportEnrichSuggestions([]);

    try {
      const payload = {
        title: String(importPreview.title || "").trim(),
        artist: String(importPreview.artist || "").trim(),
      };

      const response = await fetchWithCredentials(
        `${getSongLibraryApiBaseUrl()}/songs/import-preview-enrich`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.ok) {
        throw createLocalizedRequestError(
          result,
          tr("messages.previewEnrichFailedHttp", "Esikatselun tietojen haku epäonnistui. HTTP {{status}}", {
            status: response.status,
          })
        );
      }

      const suggestions = [
        result.suggestion,
        ...(Array.isArray(result.matches) ? result.matches : []),
      ]
        .filter(Boolean)
        .filter((item, index, array) => {
          const key = `${item.title || ""}|${item.artist || ""}|${item.year || ""}`;
          return (
            array.findIndex(
              (other) =>
                `${other.title || ""}|${other.artist || ""}|${other.year || ""}` === key
            ) === index
          );
        });

      if (!suggestions.length) {
        showStatus(tr("messages.noWebSuggestions", "Verkosta ei löytynyt ehdotuksia."));
        return;
      }

      setImportEnrichSuggestions(suggestions);

      const firstSuggestion = suggestions[0];

      setImportPreview((current) => ({
        ...(current || {}),
        title: firstSuggestion.title || current?.title || "",
        artist: firstSuggestion.artist || current?.artist || "",
        year: firstSuggestion.year || current?.year || null,
        decade: firstSuggestion.year
          ? getDecadeFromYear(firstSuggestion.year)
          : current?.decade || "",
        genre: firstSuggestion.genre || current?.genre || "",
      }));

      showStatus(tr("messages.suggestionsLoadedBestImport", "Ehdotukset haettu ja paras osuma valittu. Tarkista tiedot ennen tallennusta."));
    } catch (error) {
      console.error("Esikatselun tietojen haku ep\u00e4onnistui:", error);
      showStatus(getLocalizedErrorMessage(error, tr("messages.previewEnrichFailed", "Esikatselun tietojen haku epäonnistui.")));
    } finally {
      setImportEnrichLoading(false);
    }
  }

  function applyImportEnrichSuggestion(suggestion) {
    setImportPreview((current) => ({
      ...(current || {}),
      title: suggestion.title || current?.title || "",
      artist: suggestion.artist || current?.artist || "",
      year: suggestion.year || current?.year || null,
      decade: suggestion.year ? getDecadeFromYear(suggestion.year) : current?.decade || "",
      genre: suggestion.genre || current?.genre || "",
    }));

    showStatus(tr("messages.suggestionSelectedImport", "Ehdotus valittu. Tarkista tiedot ennen tallennusta."));
  }

  async function submitBulkSongs(event) {
    event.preventDefault();

    if (isEditing) {
      await saveEdit();
      return;
    }

    const lines = bulkSongsText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

const isChordProBatchSave = !lines.length && importPreviews.length > 0;
const isImportPreviewSave =
  !lines.length && !isChordProBatchSave && Boolean(importPreview?.title);

if (isChordProBatchSave) {
  importPreviews.forEach((preview) => {
    lines.push(
      preview.artist
        ? `${preview.title} - ${preview.artist}`
        : preview.title
    );
  });
} else if (isImportPreviewSave) {
  lines.push(
    importPreview.artist
      ? `${importPreview.title} - ${importPreview.artist}`
      : importPreview.title
  );
}

    if (!lines.length) {
      showStatus(tr("messages.addSongLineOrImport", "Lisää vähintään yksi biisirivi tai tuo biisi tiedostosta."));
      return;
    }

    try {
      const normalizedOccasionTags = Array.isArray(editForm.occasionTags)
  ? editForm.occasionTags
  : String(editForm.occasionTags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

const payload = {
  genre: (bulkGenre === "__custom__" ? customBulkGenre : bulkGenre).trim(),
  language: bulkLanguage,
  decade: bulkDecade.trim(),
  tempo: bulkTempo,
  year: bulkYear ? Number(bulkYear) : null,
  mood: [],
  items: lines,
};

const apiBaseUrl =
  window.PARANOIDI_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:4000/api"
    : "/backend/api");

const slug = String(effectivePerformerSlug || "").trim();

const url = slug
  ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/bulk`
  : `${apiBaseUrl}/songs/bulk`;

const response = await fetchWithCredentials(url, {
  method: "POST",
  headers: {
    "Content-Type": "application/json; charset=utf-8",
  },
  body: JSON.stringify(payload),
});

const result = await response.json().catch(() => null);

if (!response.ok) {
  throw createLocalizedRequestError(
    result,
    tr("messages.bulkCreateFailed", "Biisien massa-ajo epäonnistui.")
  );
}

/*
 * Backend voi palauttaa luodut biisit joko created- tai items-taulukossa.
 * Otetaan molemmat huomioon ja poistetaan mahdolliset tuplat ID:n perusteella.
 */
const createdSongs = [
  ...(Array.isArray(result?.created) ? result.created : []),
  ...(Array.isArray(result?.items) ? result.items : []),
].filter(
  (song, index, allSongs) =>
    song?.id &&
    allSongs.findIndex(
      (candidate) => String(candidate.id) === String(song.id)
    ) === index
);

      let chordProSaved = false;
      let chordProSavedCount = 0;

      if (isChordProBatchSave) {
        chordProSavedCount =
          await saveChordProContentsToImportedSongs(
            importPreviews,
            createdSongs
          );
      } else if (
        isImportPreviewSave &&
        importPreview?.sourceType === "chordpro"
      ) {
        chordProSaved =
          await saveChordProContentToImportedSong(
            importPreview,
            createdSongs
          );
      }

           const createdCount = Number(result?.created_count || 0);
      const skippedCount = Number(result?.skipped_count || 0);
      const skippedItems = Array.isArray(result?.skipped)
        ? result.skipped
        : [];

      const duplicateCount = skippedItems.filter(
        (item) => item?.reason === "duplikaatti"
      ).length;

      const onlyDuplicatesWereSkipped =
        skippedCount > 0 && duplicateCount === skippedCount;

      if (isChordProBatchSave) {
        if (createdCount === 0 && onlyDuplicatesWereSkipped) {
          const duplicateMessage =
            skippedCount === 1
              ? tr("messages.duplicateExistingOne", "Sama biisi ja esittäjä ovat jo biisikirjastossa.")
              : tr("messages.duplicateExistingMany", "{{count}} biisiä oli jo biisikirjastossa.", {
                  count: skippedCount,
                });

          showStatus(
            tr(
              "messages.noNewSongsChordProSaved",
              "Uusia biisejä ei lisätty. {{duplicateMessage}} Sanat/soinnut tallennettu {{savedCount}} biisiin.",
              {
                duplicateMessage,
                savedCount: chordProSavedCount,
              }
            )
          );
        } else {
          showStatus(
            tr(
              "messages.chordProBatchSaved",
              "Lisätty {{createdCount}} biisiä. Sanat/soinnut tallennettu {{savedCount}} biisiin. Ohitettu {{skippedCount}}.",
              {
                createdCount,
                savedCount: chordProSavedCount,
                skippedCount,
              }
            )
          );
        }
      } else if (
        isImportPreviewSave &&
        importPreview?.sourceType === "chordpro" &&
        chordProSaved &&
        createdCount > 0
      ) {
        showStatus(tr("messages.songAddedWithLyrics", "Biisi lisätty ja sanat/soinnut tallennettu."));
      } else if (
        isImportPreviewSave &&
        importPreview?.sourceType === "chordpro" &&
        chordProSaved &&
        onlyDuplicatesWereSkipped
      ) {
        showStatus(
          tr(
            "messages.duplicateLyricsUpdated",
            "Biisi oli jo kirjastossa. Sanat ja soinnut päivitettiin olemassa olevaan biisiin."
          )
        );
      } else if (
        isImportPreviewSave &&
        importPreview?.sourceType === "chordpro" &&
        createdCount > 0
      ) {
        showStatus(
          tr(
            "messages.songAddedLyricsFailed",
            "Biisi lisätty, mutta sanat/soinnut eivät vielä tallentuneet."
          )
        );
      } else if (isImportPreviewSave && createdCount > 0) {
        showStatus(tr("messages.songAdded", "Biisi lisätty kirjastoon."));
      } else if (isImportPreviewSave && onlyDuplicatesWereSkipped) {
        showStatus(
          tr(
            "messages.duplicateSongNotAdded",
            "Biisiä ei lisätty, koska sama biisi ja esittäjä ovat jo biisikirjastossa."
          )
        );
      } else if (createdCount === 0 && onlyDuplicatesWereSkipped) {
        showStatus(
          skippedCount === 1
            ? tr(
                "messages.duplicateSongNotAdded",
                "Biisiä ei lisätty, koska sama biisi ja esittäjä ovat jo biisikirjastossa."
              )
            : tr(
                "messages.duplicateSongsNotAdded",
                "{{count}} biisiä ei lisätty, koska ne olivat jo biisikirjastossa.",
                { count: skippedCount }
              )
        );
      } else {
        showStatus(
          tr(
            "messages.bulkAdded",
            "Lisätty {{createdCount}} biisiä. Ohitettu {{skippedCount}}.",
            {
              createdCount,
              skippedCount,
            }
          )
        );
      }

      setBulkSongsText("");
      setImportFile(null);
      setImportFiles([]);
      setImportPreview(null);
      setImportPreviews([]);
      setImportEnrichSuggestions([]);
      setImportUrl("");
      setImportInputResetKey((current) => current + 1);
      setIsBulkInputOpen(false);
      await refreshSongs();
    } catch (error) {
      console.error("Biisien tallennus epäonnistui:", error);

      if (error?.code === "SONG_LIMIT_REACHED" || error?.payload?.code === "SONG_LIMIT_REACHED") {
        const payload = error.payload || {};
        const plan = payload.plan || "Basic";
        const songLimit = payload.songLimit || 100;
        const currentSongCount = payload.currentSongCount;

        setSongLimitModal({
          plan,
          songLimit,
          currentSongCount,
        });

        return;
      }

      showStatus(tr("messages.songSaveFailed", "Biisien tallennus epäonnistui."));
    }
  }

  async function removeSong(song) {
    if (!song?.id) {
      return;
    }

    try {
      const apiBaseUrl =
  window.PARANOIDI_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:4000/api"
    : "/backend/api");

const storedSlug = (() => {
  try {
    return (
      JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
        ?.slug || ""
    );
  } catch {
    return "";
  }
})();

const slug = String(effectivePerformerSlug || storedSlug || "").trim();

const url = slug
  ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
  : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

console.log("DELETE DIRECT DEBUG", { id: song.id, slug, url });

const response = await fetchWithCredentials(url, {
  method: "DELETE",
});

const result = await response.json().catch(() => null);

if (!response.ok) {
  throw createLocalizedRequestError(result, tr("messages.songDeleteFailed", "Biisin poisto epäonnistui."));
}
      showStatus(tr("messages.songDeleted", "Biisi poistettu."));
      setDeleteConfirmSong(null);

      if (editingSongId === song.id) {
        cancelEdit();
      }

      await refreshSongs();
    } catch (error) {
      console.error("Biisin poisto epäonnistui:", error);
      showStatus(tr("messages.songDeleteFailed", "Biisin poisto epäonnistui."));
    }
  }


async function toggleSongRequestable(song) {
  if (!song?.id) {
    return;
  }

  const nextIsRequestable = !getSongIsRequestable(song);

  const rawOccasionTags = getSongOccasionTags(song);
  const normalizedOccasionTags = Array.isArray(rawOccasionTags)
    ? rawOccasionTags
    : String(rawOccasionTags || "")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

  try {
    const payload = {
      title: String(song.title || "").trim(),
      artist: String(song.artist || "").trim(),
      year: song.year ? Number(song.year) : null,
      decade: getSongDecade(song),
      genre: String(song.genre || "").trim(),
      language: getSongLanguage(song),
      mood: String(song.mood || "").trim(),
      occasion_tags: normalizedOccasionTags,
      occasionTags: normalizedOccasionTags,
      notes: String(song.notes || "").trim(),
      isRequestable: nextIsRequestable,
      is_requestable: nextIsRequestable,
    };

    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    const url = slug
      ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
      : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

    const response = await fetchWithCredentials(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      throw createLocalizedRequestError(result, tr("messages.songUpdateFailed", "Biisin päivitys epäonnistui."));
    }

    showStatus(
      nextIsRequestable
        ? tr("messages.songRequestableRestored", "Biisi palautettu toivottavaksi.")
        : tr("messages.songHiddenFromRequests", "Biisi piilotettu yleisön biisitoiveista.")
    );

    await refreshSongs();
  } catch (error) {
    console.error("Biisin toivottavuuden päivitys epäonnistui:", error);
    showStatus(tr("messages.songUpdateFailed", "Biisin päivitys epäonnistui."));
  }
}

  function toggleSongSelection(songId) {
  const normalizedSongId = String(songId);

  setSelectedSongIds((currentIds) => {
    if (currentIds.map(String).includes(normalizedSongId)) {
      return currentIds.filter((id) => String(id) !== normalizedSongId);
    }

    return [...currentIds, normalizedSongId];
  });
}

function toggleAllFilteredSongSelections() {
  if (!filteredSongs.length) {
    return;
  }

  if (!hasBulkSelectionFilter) {
    showStatus(
      tr(
        "messages.filterBeforeSelectingResults",
        "Rajaa ensin listaa haulla, artistilla, genrellä, vuosikymmenellä, kielellä tai näkyvyydellä."
      )
    );
    return;
  }

  // Jos kaikki nykyiset hakutulokset ovat jo valittuina,
  // poistetaan valinta kokonaan.
  if (areAllFilteredSongsSelected) {
    setSelectedSongIds([]);
    return;
  }

  // Valitaan vain nykyisen rajauksen hakutulokset.
  // Aiemmat valinnat eivät jää vahingossa mukaan.
  setSelectedSongIds(selectedFilteredSongIds);
}

function toggleAllSongSelections() {
  if (!songs.length) {
    return;
  }

  if (areAllSongsSelected) {
    setSelectedSongIds([]);
    return;
  }

  // Koko kirjasto valitaan suodattimista riippumatta.
  setSelectedSongIds(allSongIds);
}

function clearSongSelections() {
  setSelectedSongIds([]);
}

function openBulkDeleteConfirm() {
  if (isBulkUpdating) {
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectSongsToDelete", "Valitse ensin poistettavat biisit."));
    return;
  }

  setBulkDeleteConfirm({
    count: selectedSongs.length,
    songs: selectedSongs.slice(0, 6),
  });
}

async function confirmBulkDeleteSongs() {
  if (isBulkUpdating) {
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    setBulkDeleteConfirm(null);
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkDeleteConfirm(null);
  setBulkProgressMessage(
    tr("progress.deletingSongs", "Poistetaan biisejä... {{completed}}/{{total}}", {
      completed: 0,
      total: selectedSongs.length,
    })
  );

  try {
    const apiBaseUrl = getSongLibraryApiBaseUrl();

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(
            window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
          )?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(
      effectivePerformerSlug || performerSlug || storedSlug || ""
    ).trim();

    const changes = {};

    if (hasLanguageChange) {
      changes.language = bulkEditLanguage;
    }

    if (hasGenreChange) {
      changes.genre =
        bulkEditGenre === BULK_CLEAR_VALUE ? "" : bulkEditGenre;
    }

    if (hasDecadeChange) {
      changes.decade =
        bulkEditDecade === BULK_CLEAR_VALUE ? "" : bulkEditDecade;
    }

    if (hasTempoChange) {
      changes.tempo =
        bulkEditTempo === BULK_CLEAR_VALUE ? "" : bulkEditTempo;
    }

    if (hasRequestableChange) {
      changes.isRequestable =
        bulkEditRequestableMode === BULK_REQUESTABLE_TRUE;
    }

    /*
    * Tilaisuustägit tarvitsevat edelleen biisikohtaisen käsittelyn,
    * koska lisäys/poisto riippuu jokaisen biisin nykyisistä tägeistä.
    */
    const canUseFastBulkUpdate = !hasTagChange;

    if (canUseFastBulkUpdate) {
      setBulkProgressMessage(
        tr("progress.savingSongChanges", "Tallennetaan {{count}} biisin muutokset...", {
          count: selectedSongs.length,
        })
      );

      const response = await fetchWithCredentials(
        `${apiBaseUrl}/admin/performers/${encodeURIComponent(
          slug
        )}/songs/bulk-update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            ids: selectedSongs.map((song) => Number(song.id)),
            changes,
          }),
        }
      );

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw createLocalizedRequestError(
          result,
          tr("messages.bulkSaveFailed", "Massamuutosten tallennus epäonnistui.")
        );
      }
    } else {
      /*
      * Tägin lisäys/poisto käyttää vielä vanhaa biisikohtaista reittiä,
      * koska jokaisen biisin nykyiset tägit pitää säilyttää.
      */
      await runBulkUpdatesInChunks(
        selectedSongs,
        8,
        async (song) => {
          const nextSong = buildBulkUpdatedSong(song);

          const normalizedOccasionTags = Array.isArray(
            getSongOccasionTags(nextSong)
          )
            ? getSongOccasionTags(nextSong)
            : parseOccasionTags(getSongOccasionTags(nextSong));

          const payload = {
            title: String(nextSong.title || "").trim(),
            artist: String(nextSong.artist || "").trim(),
            year: nextSong.year ? Number(nextSong.year) : null,
            decade: hasDecadeChange
              ? String(nextSong.decade || "").trim()
              : getSongDecade(nextSong),
            genre: String(nextSong.genre || "").trim(),
            tempo: String(nextSong.tempo || "").trim(),
            language: getSongLanguage(nextSong),
            mood: String(nextSong.mood || "").trim(),
            occasion_tags: normalizedOccasionTags,
            occasionTags: normalizedOccasionTags,
            notes: String(nextSong.notes || "").trim(),
            isRequestable: getSongIsRequestable(nextSong),
            is_requestable: getSongIsRequestable(nextSong),
          };

          const url = slug
            ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(
                slug
              )}/songs/${encodeURIComponent(song.id)}`
            : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

          const response = await fetchWithCredentials(url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify(payload),
          });

          const result = await response.json().catch(() => null);

          if (!response.ok) {
            throw createLocalizedRequestError(
              result,
              tr("messages.bulkSaveFailed", "Massamuutosten tallennus epäonnistui.")
            );
          }
        },
        (completed) => {
          setBulkProgressMessage(
            tr("progress.savingChanges", "Tallennetaan muutoksia... {{completed}}/{{total}}", {
              completed,
              total: selectedSongs.length,
            })
          );
        }
      );
    }

    const deletedIds = new Set(selectedSongs.map((song) => String(song.id)));

    setSongs((currentSongs) =>
      currentSongs.filter((song) => !deletedIds.has(String(song.id)))
    );

    if (deletedIds.has(String(editingSongId))) {
      cancelEdit();
    }

    setSelectedSongIds([]);
    showStatus(
      tr("messages.bulkDeleted", "Poistettu {{count}} biisiä.", {
        count: selectedSongs.length,
      })
    );
    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien poisto epäonnistui:", error);
    showStatus(tr("messages.bulkDeleteFailed", "Valittujen biisien poisto epäonnistui. Tarkista lista ja yritä uudelleen."));
    setSelectedSongIds([]);
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function runBulkUpdatesInChunks(items, chunkSize, updater, onProgress) {
  let completed = 0;

  for (let index = 0; index < items.length; index += chunkSize) {
    const chunk = items.slice(index, index + chunkSize);

    await Promise.all(
      chunk.map(async (item) => {
        await updater(item);
        completed += 1;
        onProgress?.(completed);
      })
    );
  }
}

async function saveBulkChangesForSelectedSongs() {
  if (isBulkUpdating) {
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsToEdit", "Valitse ensin muokattavat biisit."));
    return;
  }

  const hasLanguageChange = bulkEditLanguage !== BULK_NO_CHANGE;
  const hasGenreChange = bulkEditGenre !== BULK_NO_CHANGE;
  const hasDecadeChange = bulkEditDecade !== BULK_NO_CHANGE;
  const hasTempoChange = bulkEditTempo !== BULK_NO_CHANGE;
  const hasTagChange = bulkEditTagAction !== BULK_TAG_NO_CHANGE;
  const hasRequestableChange =
    bulkEditRequestableMode !== BULK_REQUESTABLE_NO_CHANGE;

  if (
    !hasLanguageChange &&
    !hasGenreChange &&
    !hasDecadeChange &&
    !hasTempoChange &&
    !hasTagChange &&
    !hasRequestableChange
  ) {
    showStatus(tr("messages.selectAtLeastOneChange", "Valitse ensin vähintään yksi muutos."));
    return;
  }

  const tag = String(bulkOccasionTag || "").trim();

  if (hasTagChange && !tag) {
    showStatus(tr("messages.selectOccasionTag", "Valitse ensin tilaisuustägi."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  function buildBulkUpdatedSong(song) {
    let nextOccasionTags = getSongOccasionTags(song);

    if (bulkEditTagAction === BULK_TAG_ADD) {
      nextOccasionTags = addOccasionTag(nextOccasionTags, tag);
    }

    if (bulkEditTagAction === BULK_TAG_REMOVE) {
      nextOccasionTags = removeOccasionTag(nextOccasionTags, tag);
    }

    const nextSong = {
      ...song,
    };

    if (hasLanguageChange) {
      nextSong.language = bulkEditLanguage;
    }

    if (hasGenreChange) {
      nextSong.genre = bulkEditGenre;
    }

    if (hasDecadeChange) {
      nextSong.decade =
        bulkEditDecade === BULK_CLEAR_VALUE ? "" : bulkEditDecade;
    }

    if (hasTempoChange) {
      nextSong.tempo =
        bulkEditTempo === BULK_CLEAR_VALUE ? "" : bulkEditTempo;
    }

    if (hasTagChange) {
      nextSong.occasionTags = nextOccasionTags;
      nextSong.occasion_tags = nextOccasionTags;
    }

    if (hasRequestableChange) {
      const nextIsRequestable =
        bulkEditRequestableMode === BULK_REQUESTABLE_TRUE;

      nextSong.isRequestable = nextIsRequestable;
      nextSong.is_requestable = nextIsRequestable;
    }

    return nextSong;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.savingChanges", "Tallennetaan muutoksia... {{completed}}/{{total}}", {
      completed: 0,
      total: selectedSongs.length,
    })
  );

  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id)) ? buildBulkUpdatedSong(song) : song
    )
  );

  try {
    const apiBaseUrl = getSongLibraryApiBaseUrl();

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(
            window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
          )?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(
      effectivePerformerSlug || performerSlug || storedSlug || ""
    ).trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const nextSong = buildBulkUpdatedSong(song);
        const normalizedOccasionTags = Array.isArray(
          getSongOccasionTags(nextSong)
        )
          ? getSongOccasionTags(nextSong)
          : parseOccasionTags(getSongOccasionTags(nextSong));

        const payload = {
          title: String(nextSong.title || "").trim(),
          artist: String(nextSong.artist || "").trim(),
          year: nextSong.year ? Number(nextSong.year) : null,
          decade: hasDecadeChange
            ? String(nextSong.decade || "").trim()
            : getSongDecade(nextSong),
          genre: String(nextSong.genre || "").trim(),
          tempo: String(nextSong.tempo || "").trim(),
          language: getSongLanguage(nextSong),
          mood: String(nextSong.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(nextSong.notes || "").trim(),
          isRequestable: getSongIsRequestable(nextSong),
          is_requestable: getSongIsRequestable(nextSong),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(
              slug
            )}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(
            result,
            tr("messages.bulkSaveFailed", "Massamuutosten tallennus epäonnistui.")
          );
        }
      },
      (completed) => {
        setBulkProgressMessage(
          `Tallennetaan muutoksia... ${completed}/${selectedSongs.length}`
        );
      }
    );

    showStatus(
      tr(
        "messages.bulkChangesSaved",
        "Muutokset tallennettu {{count}} valittuun biisiin.",
        { count: selectedSongs.length }
      )
    );

    setSelectedSongIds([]);
    setBulkEditLanguage(BULK_NO_CHANGE);
    setBulkEditGenre(BULK_NO_CHANGE);
    setBulkEditDecade(BULK_NO_CHANGE);
    setBulkEditTempo(BULK_NO_CHANGE);
    setBulkEditTagAction(BULK_TAG_NO_CHANGE);
    setBulkEditRequestableMode(BULK_REQUESTABLE_NO_CHANGE);

    await refreshSongs();
  } catch (error) {
    console.error("Massamuutosten tallennus epäonnistui:", error);
    showStatus(getLocalizedErrorMessage(error, tr("messages.bulkSaveFailed", "Massamuutosten tallennus epäonnistui.")));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function setLanguageForSelectedSongs() {
  const language = String(bulkEditLanguage || "unknown").trim();

  if (isBulkUpdating) {
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsForLanguage", "Valitse ensin biisit, joiden kieli muutetaan."));
    return;
  }

  if (!language) {
    showStatus(tr("messages.selectLanguage", "Valitse kieli."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.updatingLanguage", "Päivitetään {{count}} biisin kieli...", {
      count: selectedSongs.length,
    })
  );

  // Päivitetään näkymä heti, ettei käyttäjä jää odottamaan tyhjää.
  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            language,
          }
        : song
    )
  );

  try {
    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const normalizedOccasionTags = Array.isArray(getSongOccasionTags(song))
          ? getSongOccasionTags(song)
          : String(getSongOccasionTags(song) || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade: getSongDecade(song),
          genre: String(song.genre || "").trim(),
          language,
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: getSongIsRequestable(song),
          is_requestable: getSongIsRequestable(song),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.languageUpdateFailed", "Valittujen biisien kielen päivitys epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.updatingLanguages", "Päivitetään kieliä... {{completed}}/{{total}}", {
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      tr(
        "messages.bulkLanguageSaved",
        'Kieli "{{language}}" asetettu {{count}} biisiin. Voit jatkaa samojen biisien muokkausta.',
        {
          language: getLocalizedLanguageLabel(language),
          count: selectedSongs.length,
        }
      )
    );

    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien kielen päivitys epäonnistui:", error);
    showStatus(tr("messages.languageUpdateFailed", "Valittujen biisien kielen päivitys epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function setGenreForSelectedSongs() {
  const genre = String(bulkEditGenre || "").trim();

  if (isBulkUpdating) {
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsForGenre", "Valitse ensin biisit, joiden genre muutetaan."));
    return;
  }

  if (!genre) {
    showStatus(tr("messages.selectGenre", "Valitse genre."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.updatingGenre", "Päivitetään {{count}} biisin genre...", {
      count: selectedSongs.length,
    })
  );

  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            genre,
          }
        : song
    )
  );

  try {
    const apiBaseUrl = getSongLibraryApiBaseUrl();

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const rawOccasionTags = getSongOccasionTags(song);
        const normalizedOccasionTags = Array.isArray(rawOccasionTags)
          ? rawOccasionTags
          : parseOccasionTags(rawOccasionTags);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade: getSongDecade(song),
          genre,
          language: getSongLanguage(song),
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: getSongIsRequestable(song),
          is_requestable: getSongIsRequestable(song),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.genreUpdateFailed", "Valittujen biisien genren päivitys epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.updatingGenres", "Päivitetään genreä... {{completed}}/{{total}}", {
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      tr(
        "messages.bulkGenreSaved",
        'Genre "{{genre}}" asetettu {{count}} biisiin. Voit jatkaa samojen biisien muokkausta.',
        {
          genre: getLocalizedGenreLabel(genre),
          count: selectedSongs.length,
        }
      )
    );
    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien genren päivitys epäonnistui:", error);
    showStatus(tr("messages.genreUpdateFailed", "Valittujen biisien genren päivitys epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function setDecadeForSelectedSongs() {
  const decade = String(bulkEditDecade || "").trim();

  if (isBulkUpdating) {
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsForDecade", "Valitse ensin biisit, joiden vuosikymmen muutetaan."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.updatingDecade", "Päivitetään {{count}} biisin vuosikymmen...", {
      count: selectedSongs.length,
    })
  );

  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            decade,
          }
        : song
    )
  );

  try {
    const apiBaseUrl = getSongLibraryApiBaseUrl();

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const rawOccasionTags = getSongOccasionTags(song);
        const normalizedOccasionTags = Array.isArray(rawOccasionTags)
          ? rawOccasionTags
          : parseOccasionTags(rawOccasionTags);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade,
          genre: String(song.genre || "").trim(),
          language: getSongLanguage(song),
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: getSongIsRequestable(song),
          is_requestable: getSongIsRequestable(song),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.decadeUpdateFailed", "Valittujen biisien vuosikymmenen päivitys epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.updatingDecades", "Päivitetään vuosikymmentä... {{completed}}/{{total}}", {
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      decade
        ? tr(
            "messages.bulkDecadeSaved",
            'Vuosikymmen "{{decade}}" asetettu {{count}} biisiin. Voit jatkaa samojen biisien muokkausta.',
            {
              decade: getLocalizedDecadeLabel(decade),
              count: selectedSongs.length,
            }
          )
        : tr(
            "messages.bulkDecadeCleared",
            "Vuosikymmen poistettu {{count}} biisistä. Voit jatkaa samojen biisien muokkausta.",
            { count: selectedSongs.length }
          )
    );

    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien vuosikymmenen päivitys epäonnistui:", error);
    showStatus(tr("messages.decadeUpdateFailed", "Valittujen biisien vuosikymmenen päivitys epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function removeOccasionTagFromSelectedSongs() {
  setIsBulkOccasionMenuOpen(false);

  const tag = String(bulkOccasionTag || "").trim();

  if (isBulkUpdating) {
    return;
  }

  if (!tag) {
    showStatus(tr("messages.selectOccasionTagToRemove", "Valitse ensin poistettava tilaisuustägi."));
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsForTagRemoval", "Valitse ensin biisit, joista tagi poistetaan."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.removingTag", "Poistetaan tägiä {{count}} biisistä...", {
      count: selectedSongs.length,
    })
  );

  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            occasionTags: removeOccasionTag(getSongOccasionTags(song), tag),
            occasion_tags: removeOccasionTag(getSongOccasionTags(song), tag),
          }
        : song
    )
  );

  try {
    const apiBaseUrl = getSongLibraryApiBaseUrl();

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const nextOccasionTagsValue = removeOccasionTag(getSongOccasionTags(song), tag);
        const normalizedOccasionTags = parseOccasionTags(nextOccasionTagsValue);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade: getSongDecade(song),
          genre: String(song.genre || "").trim(),
          language: getSongLanguage(song),
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: getSongIsRequestable(song),
          is_requestable: getSongIsRequestable(song),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.tagRemovalFailed", "Valittujen biisien tägin poisto epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.removingTags", "Poistetaan tägiä... {{completed}}/{{total}}", {
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      tr(
        "messages.bulkTagRemoved",
        'Tägi "{{tag}}" poistettu {{count}} biisistä. Voit jatkaa samojen biisien muokkausta.',
        {
          tag: getLocalizedOccasionTagLabel(tag),
          count: selectedSongs.length,
        }
      )
    );
    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien tägin poisto epäonnistui:", error);
    showStatus(tr("messages.tagRemovalFailed", "Valittujen biisien tägin poisto epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

async function setRequestableForSelectedSongs(nextIsRequestable) {
  if (isBulkUpdating) {
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsFirst", "Valitse ensin biisit."));
    return;
  }

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  const actionLabel = nextIsRequestable
    ? tr("progress.restoringRequestable", "Palautetaan biisejä toivottaviksi")
    : tr("progress.hidingFromRequests", "Piilotetaan biisejä toiveista");

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.generic", "{{action}}... {{completed}}/{{total}}", {
      action: actionLabel,
      completed: 0,
      total: selectedSongs.length,
    })
  );

  // Päivitetään näkymä heti.
  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            isRequestable: nextIsRequestable,
            is_requestable: nextIsRequestable,
          }
        : song
    )
  );

  try {
    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(
            window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
          )?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(
      effectivePerformerSlug || performerSlug || storedSlug || ""
    ).trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const rawOccasionTags = getSongOccasionTags(song);

        const normalizedOccasionTags = Array.isArray(rawOccasionTags)
          ? rawOccasionTags
          : String(rawOccasionTags || "")
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade: getSongDecade(song),
          genre: String(song.genre || "").trim(),
          language: getSongLanguage(song),
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: nextIsRequestable,
          is_requestable: nextIsRequestable,
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(
              slug
            )}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.songUpdateFailed", "Biisin päivitys epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.generic", "{{action}}... {{completed}}/{{total}}", {
            action: actionLabel,
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      nextIsRequestable
        ? tr(
            "messages.bulkRequestableShown",
            "{{count}} biisiä asetettu toivottaviksi. Voit jatkaa samojen biisien muokkausta.",
            { count: selectedSongs.length }
          )
        : tr(
            "messages.bulkRequestableHidden",
            "{{count}} biisiä piilotettu yleisön biisitoiveista. Voit jatkaa samojen biisien muokkausta.",
            { count: selectedSongs.length }
          )
    );

    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien toivottavuuden päivitys epäonnistui:", error);
    showStatus(tr("messages.requestableUpdateFailed", "Valittujen biisien toivottavuuden päivitys epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

function addOccasionTagToSelectedSongs() {
  setIsBulkOccasionMenuOpen(false);

  const tag = String(bulkOccasionTag || "").trim();

  if (!tag) {
    showStatus(tr("messages.selectOccasionTag", "Valitse ensin tilaisuustägi."));
    return;
  }

  if (!selectedSongIds.length) {
    showStatus(tr("messages.selectSongsForTagAdd", "Valitse ensin biisit, joihin tagi lisätään."));
    return;
  }

  const selectedSongs = songs.filter((song) =>
    selectedSongIds.map(String).includes(String(song.id))
  );

  if (!selectedSongs.length) {
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setTagConfirmSelection({
    tag,
    count: selectedSongs.length,
  });
}

async function confirmAddOccasionTagToSelectedSongs() {
  if (isBulkUpdating) {
    return;
  }

  if (!tagConfirmSelection?.tag) {
    return;
  }

  const tag = tagConfirmSelection.tag;

  const selectedIds = new Set(selectedSongIds.map(String));

  const selectedSongs = songs.filter((song) =>
    selectedIds.has(String(song.id))
  );

  if (!selectedSongs.length) {
    setTagConfirmSelection(null);
    showStatus(tr("messages.selectedSongsNotFound", "Valittuja biisejä ei löytynyt."));
    return;
  }

  setIsBulkUpdating(true);
  setBulkProgressMessage(
    tr("progress.addingTag", "Lisätään tägiä {{count}} biisiin...", {
      count: selectedSongs.length,
    })
  );

  // Päivitetään näkymä heti.
  setSongs((currentSongs) =>
    currentSongs.map((song) =>
      selectedIds.has(String(song.id))
        ? {
            ...song,
            occasionTags: addOccasionTag(getSongOccasionTags(song), tag),
            occasion_tags: addOccasionTag(getSongOccasionTags(song), tag),
          }
        : song
    )
  );

  try {
    const apiBaseUrl =
      window.PARANOIDI_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "http://localhost:4000/api"
        : "/backend/api");

    const storedSlug = (() => {
      try {
        return (
          JSON.parse(window.sessionStorage.getItem("paranoidi_auth_performer") || "{}")
            ?.slug || ""
        );
      } catch {
        return "";
      }
    })();

    const slug = String(effectivePerformerSlug || performerSlug || storedSlug || "").trim();

    await runBulkUpdatesInChunks(
      selectedSongs,
      8,
      async (song) => {
        const nextOccasionTagsValue = addOccasionTag(getSongOccasionTags(song), tag);

        const normalizedOccasionTags = Array.isArray(nextOccasionTagsValue)
          ? nextOccasionTagsValue
          : String(nextOccasionTagsValue || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);

        const payload = {
          title: String(song.title || "").trim(),
          artist: String(song.artist || "").trim(),
          year: song.year ? Number(song.year) : null,
          decade: getSongDecade(song),
          genre: String(song.genre || "").trim(),
          language: getSongLanguage(song),
          mood: String(song.mood || "").trim(),
          occasion_tags: normalizedOccasionTags,
          occasionTags: normalizedOccasionTags,
          notes: String(song.notes || "").trim(),
          isRequestable: getSongIsRequestable(song),
          is_requestable: getSongIsRequestable(song),
        };

        const url = slug
          ? `${apiBaseUrl}/admin/performers/${encodeURIComponent(slug)}/songs/${encodeURIComponent(song.id)}`
          : `${apiBaseUrl}/songs/${encodeURIComponent(song.id)}`;

        const response = await fetchWithCredentials(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw createLocalizedRequestError(result, tr("messages.tagAddFailed", "Valittujen biisien tägäys epäonnistui."));
        }
      },
      (completed) => {
        setBulkProgressMessage(
          tr("progress.addingTags", "Lisätään tägiä... {{completed}}/{{total}}", {
            completed,
            total: selectedSongs.length,
          })
        );
      }
    );

    showStatus(
      tr(
        "messages.bulkTagAdded",
        'Tägi "{{tag}}" lisätty {{count}} biisiin. Voit jatkaa samojen biisien muokkausta.',
        {
          tag: getLocalizedOccasionTagLabel(tag),
          count: selectedSongs.length,
        }
      )
    );
    setTagConfirmSelection(null);
    await refreshSongs();
  } catch (error) {
    console.error("Valittujen biisien tägäys epäonnistui:", error);
    showStatus(tr("messages.tagAddFailed", "Valittujen biisien tägäys epäonnistui."));
    await refreshSongs();
  } finally {
    setIsBulkUpdating(false);
    setBulkProgressMessage("");
  }
}

  return (
    <div className="mt-6 grid min-w-0 max-w-full gap-4 overflow-hidden sm:gap-6 sm:px-0">

      {bulkProgressMessage &&
        createPortal(
          <div className="fixed bottom-5 left-4 right-4 z-[150000] rounded-2xl border border-[#ef4444]/30 bg-zinc-950 px-5 py-4 text-center text-sm font-black text-[#fecaca] shadow-2xl shadow-black/30 sm:left-auto sm:right-6 sm:max-w-sm">
            {bulkProgressMessage}
          </div>,
          document.body
        )}

      {songLimitModal &&
        createPortal(
          <div className="fixed inset-0 z-[130000] grid place-items-center bg-black/65 px-4 backdrop-blur-sm">
            <div className="relative z-[130001] w-full max-w-lg rounded-[2rem] border border-red-500/30 bg-[#fff8ef] p-6 text-center shadow-2xl shadow-black/30 theme-dark:bg-zinc-950">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-red-600 theme-dark:text-red-300">
                {tr("limit.title", "Pakettiraja täynnä")}
              </p>

              <h3 className="mt-3 text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                {tr(
                  "limit.planTitle",
                  "{{plan}}-paketin biisiraja on täynnä",
                  { plan: songLimitModal.plan }
                )}
              </h3>

              <p className="mt-4 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                {tr(
                  "limit.allowedSongs",
                  "Nykyinen paketti sallii {{limit}} biisiä.",
                  { limit: songLimitModal.songLimit }
                )}
                {songLimitModal.currentSongCount !== undefined
                  ? ` ${tr(
                      "limit.currentCount",
                      "Kirjastossa on nyt {{count}} biisiä.",
                      { count: songLimitModal.currentSongCount }
                    )}`
                  : ""}
              </p>

              <p className="mt-3 text-sm font-semibold leading-7 text-[#241a16] theme-dark:text-white">
                {tr("limit.upgradeHint", "Päivitä Plus-pakettiin, jos haluat lisätä enemmän biisejä.")}
              </p>

              <div className="mt-6 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setSongLimitModal(null)}
                  className="rounded-full border border-[#5a3824]/15 bg-white/70 px-5 py-3 text-sm font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                >
                  {tr("actions.close", "Sulje")}
                </button>

                <a
                  href="/admin/subscription"
                  className="rounded-full border border-red-300 bg-red-50 px-5 py-3 text-sm font-semibold text-red-700 no-underline transition hover:bg-red-100 theme-dark:border-red-500/30 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15"
                >
                  {tr("actions.viewPlans", "Katso paketit")}
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}

      {deleteConfirmSong &&
        createPortal(
          <div className="fixed inset-0 z-[100000] grid place-items-center bg-black/55 px-4 backdrop-blur-sm">
            <div className="relative z-[100001] w-full max-w-md rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef] p-6 shadow-2xl shadow-black/25 theme-dark:border-[#ef4444]/20 theme-dark:bg-zinc-950">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                {tr("deleteConfirm.eyebrow", "Vahvista poisto")}
              </p>

              <h3 className="mt-3 text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
                {tr("deleteConfirm.title", "Poistetaanko biisi kirjastosta?")}
              </h3>

              <p className="mt-3 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                {tr("deleteConfirm.description", "Tätä toimintoa ei voi perua. Poistettava biisi:")}
              </p>

              <div className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-white/70 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.04]">
                <p className="text-sm font-black text-[#241a16] theme-dark:text-white">
                  {deleteConfirmSong.title}
                </p>

                {deleteConfirmSong.artist && (
                  <p className="mt-1 text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                    {deleteConfirmSong.artist}
                  </p>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmSong(null)}
                  className="rounded-full border border-[#5a3824]/15 bg-white/70 px-5 py-2.5 text-sm font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                >
                  {tr("actions.cancel", "Peruuta")}
                </button>

                <button
                  type="button"
                  onClick={() => removeSong(deleteConfirmSong)}
                  className="rounded-full border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                >
                  {tr("actions.deleteSong", "Poista biisi")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {bulkDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[110000] grid place-items-center bg-black/75 px-4 backdrop-blur-sm">
            <div className="relative z-[110001] w-full max-w-lg overflow-hidden rounded-[2rem] border border-[#ef4444]/35 bg-[#050605] text-white shadow-2xl shadow-black/50">
              <div className="border-b border-white/10 bg-[#ef4444]/10 px-6 py-5">
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#ef4444]">
                  {tr("bulkDelete.eyebrow", "Massapoisto")}
                </p>

                <h3 className="mt-3 text-2xl font-black tracking-[-0.04em] text-white">
                  {tr(
                    "bulkDelete.title",
                    "Poistetaanko {{count}} biisiä kirjastosta?",
                    { count: bulkDeleteConfirm.count }
                  )}
                </h3>

                <p className="mt-3 text-sm font-semibold leading-6 text-zinc-300">
                  {tr(
                    "bulkDelete.description",
                    "Tämä poistaa valitut biisit pysyvästi esiintyjän biisikirjastosta. Toimintoa ei voi perua."
                  )}
                </p>
              </div>

              <div className="px-6 py-5">
                <div className="rounded-[1.5rem] border border-[#ef4444]/25 bg-[#ef4444]/10 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#fecaca]">
                    {tr("bulkDelete.listTitle", "Poistettavat biisit")}
                  </p>

                  <ul className="mt-3 grid max-h-56 gap-2 overflow-auto pr-1">
                    {bulkDeleteConfirm.songs.map((song) => (
                      <li
                        key={song.id}
                        className="rounded-2xl border border-white/10 bg-black/25 px-4 py-3"
                      >
                        <p className="break-words text-sm font-black text-white">
                          {song.title}
                        </p>

                        {song.artist && (
                          <p className="mt-1 break-words text-xs font-semibold text-zinc-400">
                            {song.artist}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>

                  {bulkDeleteConfirm.count > bulkDeleteConfirm.songs.length && (
                    <p className="mt-3 text-xs font-semibold leading-5 text-zinc-400">
                      {tr(
                        "bulkDelete.moreSongs",
                        "Lisäksi {{count}} muuta valittua biisiä.",
                        {
                          count:
                            bulkDeleteConfirm.count -
                            bulkDeleteConfirm.songs.length,
                        }
                      )}
                    </p>
                  )}
                </div>

                <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setBulkDeleteConfirm(null)}
                    disabled={isBulkUpdating}
                    className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm font-black text-zinc-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {tr("bulkDelete.cancel", "Ei poisteta")}
                  </button>

                  <button
                    type="button"
                    onClick={confirmBulkDeleteSongs}
                    disabled={isBulkUpdating}
                    className="rounded-full border border-[#ef4444]/50 bg-[#ef4444] px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-[#ef4444]/20 transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isBulkUpdating
                      ? tr("actions.deleting", "Poistetaan...")
                      : tr(
                          "bulkDelete.deleteCount",
                          "Poista {{count}} biisiä",
                          { count: bulkDeleteConfirm.count }
                        )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {tagConfirmSelection &&
  createPortal(
    <div className="fixed inset-0 z-[100000] grid place-items-center bg-black/55 px-4 backdrop-blur-sm">
      <div className="relative z-[100001] w-full max-w-md rounded-[2rem] border border-[#5a3824]/15 bg-[#fff8ef] p-6 shadow-2xl shadow-black/25 theme-dark:border-[#ef4444]/20 theme-dark:bg-zinc-950">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
          {tr("tagConfirm.eyebrow", "Vahvista tägäys")}
        </p>

        <h3 className="mt-3 text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
          {tr("tagConfirm.title", "Lisätäänkö tagi valittuihin biiseihin?")}
        </h3>

        <p className="mt-3 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
          {tr("tagConfirm.tagPrefix", "Tilaisuustägi")}{" "}
          <span className="font-black text-[#241a16] theme-dark:text-white">
            {getLocalizedOccasionTagLabel(tagConfirmSelection.tag)}
          </span>{" "}
          {tr(
            "tagConfirm.addedToCount",
            "lisätään {{count}} valittuun biisiin.",
            { count: tagConfirmSelection.count }
          )}
        </p>

        <div className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-white/70 px-4 py-3 theme-dark:border-white/10 theme-dark:bg-white/[0.04]">
          <p className="text-xs leading-6 text-[#6b4229] theme-dark:text-zinc-400">
            {tr("tagConfirm.description", "Tämä ei poista biiseiltä vanhoja tageja, vaan lisää uuden tagin nykyisten lisäksi.")}
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setTagConfirmSelection(null)}
            className="rounded-full border border-[#5a3824]/15 bg-white/70 px-5 py-2.5 text-sm font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
          >
            {tr("actions.cancel", "Peruuta")}
          </button>

          <button
            type="button"
            onClick={confirmAddOccasionTagToSelectedSongs}
            disabled={isBulkUpdating}
            className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
          >
            {isBulkUpdating
              ? tr("actions.adding", "Lisätään...")
              : tr("actions.addOccasionTag", "Lisää tilaisuustägi")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

      <datalist id="song-artist-options">
        {artistOptions.map((artist) => (
          <option key={artist} value={artist} />
        ))}
      </datalist>

      <datalist id="song-genre-options">
        {genreOptions.map((genre) => (
          <option key={genre} value={genre} />
        ))}
      </datalist>

      <datalist id="song-year-options">
        {yearOptions.map((year) => (
          <option key={year} value={year} />
        ))}
      </datalist>

      <datalist id="song-decade-options">
        {decadeOptions.map((decade) => (
          <option key={decade} value={decade} />
        ))}
      </datalist>

        <section
          ref={editorRef}
          className="min-w-0 max-w-full overflow-hidden rounded-none border-y border-[#5a3824]/12 bg-[#fff8ef]/80 px-3 py-6 shadow-none theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] lg:rounded-[2rem] lg:border lg:p-8 lg:shadow-[0_18px_50px_rgba(58,43,24,0.10)]"
        >
        <div className="grid gap-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                {tr("header.eyebrow", "Biisikirjasto")}
              </p>

              <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
                {isEditing
                  ? tr("header.editTitle", "Muokkaa biisiä")
                  : tr("header.addTitle", "Lisää biisejä kirjastoon")}
              </h2>

              <p className="mt-3 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-400">
                {isEditing
                  ? tr(
                      "header.editDescription",
                      "Muokkaa biisin tiedot tässä yläkortissa ja tallenna muutokset nopeasti."
                    )
                  : canUseSongImport
                    ? tr(
                        "header.addDescriptionImport",
                        "Valitse ensin lisäystapa: tuo biisi PDF/DOCX/PRO-tiedostosta tai lisää biisejä käsin. Sen jälkeen voit täydentää lisättävien biisien yhteiset tiedot, kuten genren ja vuosikymmenen."
                      )
                    : tr(
                        "header.addDescriptionManual",
                        "Lisää biisejä käsin. Sen jälkeen voit täydentää lisättävien biisien yhteiset tiedot, kuten genren ja vuosikymmenen."
                      )}
              </p>
            </div>

            {isEditing && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleEnrichCurrentSong}
                  disabled={isEnriching}
                  className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#6b4229] disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                >
                  {isEnriching
                    ? tr("actions.searching", "Haetaan...")
                    : tr("actions.fetchSuggestions", "Hae ehdotukset")}
                </button>

                <button
                  type="button"
                  onClick={cancelEdit}
                  className="rounded-full border border-[#5a3824]/15 bg-white/60 px-5 py-2.5 text-sm font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                >
                  {tr("actions.cancelEdit", "Peruuta muokkaus")}
                </button>
              </div>
            )}
          </div>

          <form onSubmit={submitBulkSongs} className="grid gap-4">
            {isEditing ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("form.songTitle", "Biisin nimi")}
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          title: event.target.value,
                        }))
                      }
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("form.artist", "Artisti")}
                    <input
                      type="text"
                      list="song-artist-options"
                      value={editForm.artist}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          artist: event.target.value,
                        }))
                      }
                      placeholder={tr("form.artistPlaceholder", "Kirjoita artisti, esim. Green Day")}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    />
                  </label>
                </div>

                <div className="grid gap-3 md:grid-cols-4">
                <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("form.decade", "Vuosikymmen")}
                  <select
                    value={editForm.decade || ""}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        decade: event.target.value,
                      }))
                    }
                    className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                  >
                    <option value="">
                      {tr("form.selectDecade", "Valitse vuosikymmen")}
                    </option>
                    <option value="1950-luku">{getLocalizedDecadeLabel("1950-luku")}</option>
                    <option value="1960-luku">{getLocalizedDecadeLabel("1960-luku")}</option>
                    <option value="1970-luku">{getLocalizedDecadeLabel("1970-luku")}</option>
                    <option value="1980-luku">{getLocalizedDecadeLabel("1980-luku")}</option>
                    <option value="1990-luku">{getLocalizedDecadeLabel("1990-luku")}</option>
                    <option value="2000-luku">{getLocalizedDecadeLabel("2000-luku")}</option>
                    <option value="2010-luku">{getLocalizedDecadeLabel("2010-luku")}</option>
                    <option value="2020-luku">{getLocalizedDecadeLabel("2020-luku")}</option>
                  </select>
                </label>

                <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("form.genre", "Genre")}
                  <select
                    value={editForm.genre || "Yleinen"}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        genre: event.target.value,
                      }))
                    }
                    className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                  >
                    {genreOptions.map((genre) => (
                      <option key={genre} value={genre}>
                        {getLocalizedGenreLabel(genre)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("form.tempo", "Tempo")}
                  <select
                    value={editForm.tempo || ""}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        tempo: event.target.value,
                      }))
                    }
                    className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                  >
                    <option value="">
                      {tr("common.notDefined", "Ei määritelty")}
                    </option>
                    <option value="calm">{tr("tempo.calm", "Rauhallinen")}</option>
                    <option value="medium">{tr("tempo.medium", "Keskitempo")}</option>
                    <option value="upbeat">{tr("tempo.upbeat", "Menevä")}</option>
                  </select>
                </label>

                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("form.language", "Kieli")}
                    <select
                      value={editForm.language}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          language: event.target.value,
                        }))
                      }
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {getLocalizedLanguageLabel(option.value)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="flex min-h-[4.85rem] items-center gap-3 rounded-2xl border border-[#5a3824]/15 bg-white/70 px-4 py-3 text-sm font-semibold text-[#241a16] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white">
                    <input
                      type="checkbox"
                      checked={editForm.isRequestable}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          isRequestable: event.target.checked,
                        }))
                      }
                      className="h-5 w-5 accent-[#5a3824] theme-dark:accent-[#ef4444]"
                    />

                    <span>
                      <span className="block">{tr("requestability.requestable", "Toivottavissa")}</span>
                      <span className="mt-1 block text-xs font-normal leading-5 text-[#6b4229] theme-dark:text-zinc-400">
                        {tr("requestability.help", "Jos ruksi poistetaan, biisi ei näy yleisön biisitoiveissa.")}
                      </span>
                    </span>
                  </label>
                </div>

                                <div className="rounded-[1.5rem] border border-[#5a3824]/12 bg-white/55 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                  <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("form.occasionTags", "Tilaisuustagit")}
                  </p>

                  <p className="mt-1 text-xs leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                   {tr("form.occasionTagsHelp", "Valitse, millaisiin keikkoihin biisi sopii. Näitä käytetään settilistojen luonnissa.")}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {localizedOccasionOptions.map((tag) => {
                      const isSelected = parseOccasionTags(editForm.occasionTags).includes(tag);

                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() =>
                            setEditForm((current) => ({
                              ...current,
                              occasionTags: toggleOccasionTag(current.occasionTags, tag),
                            }))
                          }
                          className={[
                            "rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] transition",
                            isSelected
                              ? getOccasionTagClass(tag)
                              : "border-[#5a3824]/15 bg-white/70 text-[#5a3824] hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10",
                          ].join(" ")}
                        >
                          {getLocalizedOccasionTagLabel(tag)}
                        </button>
                      );
                    })}
                  </div>

                  <label className="mt-4 grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("form.customOccasionTags", "Omat tilaisuustagit")}
                    <input
                      type="text"
                      value={editForm.occasionTags}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          occasionTags: event.target.value,
                        }))
                      }
                      placeholder={tr("form.occasionTagsPlaceholder", "esim. häät, pubi, laiva")}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    />
                  </label>
                </div>
              </>
            ) : (
              <>
                {(isBulkInputOpen || importPreview) && (
                  <>
                    <div className="order-4 grid gap-3 md:grid-cols-3">
                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("bulkAdd.commonGenre", "Yhteinen genre lisättäville biiseille")}
                    <select
                      value={bulkGenre}
                      onChange={(event) => setBulkGenre(event.target.value)}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    >
                      {PRESET_GENRES.map((genre) => (
                        <option key={genre} value={genre}>
                          {getLocalizedGenreLabel(genre)}
                        </option>
                      ))}
                      <option value="__custom__">
                        {tr("bulkAdd.customGenreOption", "Muu genre...")}
                      </option>
                    </select>

                    {bulkGenre === "__custom__" && (
                      <input
                        type="text"
                        value={customBulkGenre}
                        onChange={(event) => setCustomBulkGenre(event.target.value)}
                        placeholder={tr("bulkAdd.customGenrePlaceholder", "Kirjoita oma genre")}
                        className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                      />
                    )}
                  </label>

                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("bulkAdd.commonLanguage", "Yhteinen kieli")}
                    <select
                      value={bulkLanguage}
                      onChange={(event) => setBulkLanguage(event.target.value)}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {getLocalizedLanguageLabel(option.value)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("bulkAdd.commonDecade", "Yhteinen vuosikymmen")}
                    <select
                      value={bulkDecade}
                      onChange={(event) => setBulkDecade(event.target.value)}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    >
                      <option value="">
                        {tr("common.noDecade", "Ei vuosikymmentä")}
                      </option>
                      {decadeOptions.map((decade) => (
                        <option key={decade} value={decade}>
                          {getLocalizedDecadeLabel(decade)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("bulkAdd.commonTempo", "Yhteinen tempo")}
                    <select
                      value={bulkTempo}
                      onChange={(event) => setBulkTempo(event.target.value)}
                      className="rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
                    >
                      <option value="">
                      {tr("common.notDefined", "Ei määritelty")}
                    </option>
                      <option value="calm">{tr("tempo.calm", "Rauhallinen")}</option>
                      <option value="medium">{tr("tempo.medium", "Keskitempo")}</option>
                      <option value="upbeat">{tr("tempo.upbeat", "Menevä")}</option>
                    </select>
                  </label>
                </div>
                </>
              )}
              </>
            )}

            {isEditing && enrichSuggestions.length > 0 && (
              <div className="rounded-[1.5rem] border border-[#5a3824]/12 bg-white/60 p-4 theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04]">
                <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("suggestions.title", "Verkosta löytyneet ehdotukset")}
                </p>

                <div className="mt-3 grid gap-2">
                  {enrichSuggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.title || ""}-${suggestion.artist || ""}-${index}`}
                      type="button"
                      onClick={() => applyEnrichSuggestion(suggestion)}
                      className="rounded-2xl border border-[#5a3824]/12 bg-white/70 px-4 py-3 text-left transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:hover:bg-white/10"
                    >
                      <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                        {suggestion.title || editForm.title}
                      </span>

                      <span className="mt-1 block text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                        {[
                          suggestion.artist,
                          suggestion.year,
                          suggestion.genre,
                        ]
                          .filter(Boolean)
                          .join(" · ") || tr("common.noAdditionalInfo", "Ei lisätietoja")}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {canUseSongImport && !isEditing && (
              <div className="order-3 rounded-[1.5rem] border border-[#5a3824]/10 bg-white/45 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
               <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("import.driveTitle", "2. Tuo tiedosto Google Drive -linkistä")}
                </p>

                <button
                  type="button"
                  onClick={() => setIsCloudImportInfoOpen((current) => !current)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#5a3824]/15 bg-white/70 text-xs font-black text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-zinc-200 theme-dark:hover:bg-white/10"
                  aria-label={tr("import.driveHelpAria", "Näytä Google Drive -tuonnin ohje")}
                  aria-expanded={isCloudImportInfoOpen}
                >
                  i
                </button>
              </div>

              <p className="mt-1 text-xs leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                {tr("import.driveDescription", "Liitä jaettu Google Drive -linkki.")}
              </p>

              {isCloudImportInfoOpen && (
                <div className="mt-3 rounded-2xl border border-[#5a3824]/10 bg-white/65 p-4 text-xs leading-6 text-[#5a3824] theme-dark:border-[#ef4444]/20 theme-dark:bg-black/20 theme-dark:text-zinc-300">
                  <p>
                    {tr("import.driveHelpUpload", "Lataa tiedosto Google Driveen ja jaa se niin, että linkin saanut voi avata sen.")}
                  </p>

                  <p className="mt-2">
                    <strong className="font-black text-[#241a16] theme-dark:text-white">
                      Google Drive:
                    </strong>{" "}
                    {tr(
                      "import.driveSharingSteps",
                      "valitse tiedostosta Jaa → Yleinen käyttöoikeus → Kaikki linkin saaneet → Katsoja."
                    )}
                  </p>

                  <p className="mt-2">
                    {tr("import.driveFallback", "Jos linkkituonti ei onnistu, tuo tiedosto kohdasta “Tuo tiedosto laitteelta”.")}
                  </p>
                </div>
              )}

                <div className="mt-4 grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <input
                    type="url"
                    value={importUrl}
                    onChange={(event) => {
                      setImportUrl(event.target.value);
                      setImportPreview(null);
                      setImportPreviews([]);
                    }}
                    placeholder="https://drive.google.com/..."
                    className="min-w-0 w-full rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 text-sm font-semibold text-[#241a16] outline-none theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white"
                  />

                  <button
                    type="button"
                    onClick={previewImportUrl}
                    disabled={importUrlLoading}
                    className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                  >
                    {importUrlLoading
                      ? tr("actions.reading", "Luetaan...")
                      : tr("actions.readLink", "Lue linkki")}
                  </button>
                </div>
              </div>
            )}

            {canUseSongImport && !isEditing && (
              <div className="order-1 rounded-[1.5rem] border border-[#5a3824]/10 bg-white/45 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                <p className="text-sm font-semibold text-[#241a16] theme-dark:text-white">
                  {tr("import.fileTitle", "1. Tuo biisi PDF-, DOCX- tai SongBook/ChordPro-tiedostosta")}
                </p>

                <p className="mt-1 text-xs leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                  {tr("import.fileDescription", "Valitse tiedosto ja tarkista poimitut tiedot ennen tallennusta kirjastoon. SongBook/ChordPro .pro -tiedostosta voidaan poimia myös sanat ja soinnut.")}
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div className="grid min-w-0 gap-2">
                    <input
                      ref={importFileInputRef}
                      key={importInputResetKey}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.pro,.cho,.chordpro,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        const firstFile = files[0] || null;

                        setImportFiles(files);
                        setImportFile(firstFile);
                        setImportPreview(null);
                        setImportPreviews([]);
                      }}
                      className="sr-only"
                    />

                    <div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-[#5a3824]/15 bg-white/80 p-2 theme-dark:border-white/10 theme-dark:bg-black/20 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={() => importFileInputRef.current?.click()}
                        className="shrink-0 rounded-xl border border-[#5a3824]/20 bg-[#fff8ef] px-4 py-2.5 text-xs font-black uppercase tracking-[0.08em] text-[#5a3824] transition hover:border-red-300 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 theme-dark:border-white/15 theme-dark:bg-white/[0.05] theme-dark:text-zinc-100 theme-dark:hover:border-red-400/40 theme-dark:hover:bg-red-500/10 theme-dark:hover:text-red-200"
                      >
                        {tr("import.chooseFiles", "Valitse tiedostot")}
                      </button>

                      <span
                        className="min-w-0 flex-1 truncate px-2 text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400"
                        title={
                          importFiles.length === 1
                            ? importFiles[0]?.name || ""
                            : undefined
                        }
                      >
                        {importFiles.length === 0
                          ? tr("import.noFilesSelected", "Ei tiedostoja valittu")
                          : importFiles.length === 1
                            ? importFiles[0]?.name
                            : tr(
                                "import.selectedFilesShort",
                                "{{count}} tiedostoa valittu",
                                { count: importFiles.length }
                              )}
                      </span>
                    </div>

                    {importFiles.length > 1 && (
                      <p className="text-xs font-semibold leading-5 text-[#6b4229] theme-dark:text-zinc-400">
                        {tr(
                          "import.filesSelected",
                          "Valittu {{count}} tiedostoa. PRO/SongBook-tiedostot voidaan lukea ja lisätä kerralla.",
                          { count: importFiles.length }
                        )}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={previewImportFile}
                    disabled={importLoading}
                    className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                  >
                    {importLoading
                      ? tr("actions.reading", "Luetaan...")
                      : importFiles.length > 1
                        ? tr("actions.readSelectedFiles", "Lue valitut tiedostot")
                        : tr("actions.readFile", "Lue tiedosto")}
                  </button>
                </div>

                {importPreviews.length > 1 && (
                  <div className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-[#fff8ef]/80 p-4 text-sm text-[#5a3824] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                    <p className="font-semibold text-[#241a16] theme-dark:text-white">
                      {tr("import.batchPreviewTitle", "SongBook/ChordPro-esikatselu")}
                    </p>

                    <p className="mt-2 text-xs leading-6 opacity-80">
                      {tr(
                        "import.batchPreviewDescription",
                        "Luettu {{count}} biisiä. Tallennus lisää biisit kirjastoon ja yrittää tallentaa sanat/soinnut jokaiselle biisille.",
                        { count: importPreviews.length }
                      )}
                    </p>

                    <div className="mt-3 max-h-56 overflow-auto rounded-2xl border border-[#5a3824]/10 bg-white/60 p-3 theme-dark:border-white/10 theme-dark:bg-black/20">
                      <ul className="grid gap-2">
                        {importPreviews.slice(0, 80).map((preview, index) => (
                          <li
                            key={`${preview.title}-${preview.artist}-${index}`}
                            className="text-xs leading-5"
                          >
                            <span className="font-black text-[#241a16] theme-dark:text-white">
                              {index + 1}. {preview.title}
                            </span>
                            {preview.artist ? ` – ${preview.artist}` : ""}
                          </li>
                        ))}
                      </ul>

                      {importPreviews.length > 80 && (
                        <p className="mt-3 text-xs font-semibold opacity-70">
                          {tr(
                          "import.morePreviewSongs",
                          "Lisäksi {{count}} biisiä.",
                          { count: importPreviews.length - 80 }
                        )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {importPreviews.length > 1 && (
                  <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 p-4">
                    <p className="text-sm font-black text-white">
                      {tr(
                        "import.batchReady",
                        "{{count}} SongBook/ChordPro-biisiä valmiina tallennettavaksi.",
                        { count: importPreviews.length }
                      )}
                    </p>

                    <p className="mt-1 text-xs font-semibold leading-5 text-zinc-400">
                      {tr("import.batchReadyDescription", "Tarkista lista ja tallenna biisit kirjastoon. Sanat ja soinnut tallennetaan biisien tietoihin.")}
                    </p>

                    <button
                      type="submit"
                      className="mt-4 w-full rounded-full border border-red-400/40 bg-red-500 px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-red-400 sm:w-auto"
                    >
                      {tr(
                        "import.saveBatch",
                        "Tallenna {{count}} biisiä kirjastoon",
                        { count: importPreviews.length }
                      )}
                    </button>
                  </div>
                )}

                {importPreview && importPreviews.length <= 1 && (
                  <div className="mt-4 rounded-2xl border border-[#5a3824]/12 bg-[#fff8ef]/80 p-4 text-sm text-[#5a3824] theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                    <p className="font-semibold text-[#241a16] theme-dark:text-white">
                      {tr("import.previewTitle", "Esikatselu")}
                    </p>

                    <div className="mt-3 grid gap-2">
                      <p>
                        <strong>{tr("import.songLabel", "Biisi:")}</strong> {importPreview.title || tr("common.notRecognized", "Ei tunnistettu")}
                      </p>

                      <p>
                        <strong>{tr("import.artistLabel", "Artisti:")}</strong> {importPreview.artist || tr("common.notRecognized", "Ei tunnistettu")}
                      </p>

                      <p>
                        <strong>{tr("import.keyLabel", "Sävellaji:")}</strong> {importPreview.key || tr("common.notRecognized", "Ei tunnistettu")}
                      </p>

                      {importPreview.raw_header && (
                        <p className="text-xs opacity-80">
                          <strong>{tr("import.readTitleLabel", "Luettu otsikko:")}</strong> {importPreview.raw_header}
                        </p>
                      )}

                      {importPreview.duration && (
                        <p>
                          <strong>{tr("import.durationLabel", "Kesto:")}</strong> {importPreview.duration}
                        </p>
                      )}

                      {importPreview.sourceType === "chordpro" && (
                        <div className="mt-3 rounded-2xl border border-[#5a3824]/10 bg-white/60 p-3 theme-dark:border-white/10 theme-dark:bg-black/20">
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                            {tr("lyrics.title", "Sanat ja soinnut")}
                          </p>

                          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words text-xs leading-5 text-[#241a16] theme-dark:text-zinc-300">
                            {importPreview.chordProContent}
                          </pre>
                        </div>
                      )}

                      {importPreview.sourceType === "chordpro" && importPreviews.length <= 1 && (
                        <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 p-4">
                          <p className="text-sm font-black">
                            {tr("import.singleReady", "Biisi ja sanat/soinnut valmiina tallennettavaksi.")}
                          </p>

                          <button
                            type="submit"
                            className="mt-4 w-full rounded-full border border-red-400/40 bg-red-500 px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-red-400 sm:w-auto"
                          >
                            {tr("actions.saveSongToLibrary", "Tallenna biisi kirjastoon")}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleImportPreviewEnrich}
                        disabled={importEnrichLoading}
                        className="rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#6b4229] disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
                      >
                        {importEnrichLoading
                          ? tr("actions.searching", "Haetaan...")
                          : tr("actions.fetchFromWeb", "Hae tiedot verkosta")}
                      </button>
                    </div>

                    {importEnrichSuggestions.length > 0 && (
                      <div className="mt-4 grid gap-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                          {tr("suggestions.chooseCorrect", "Valitse oikea ehdotus")}
                        </p>

                        {importEnrichSuggestions.map((suggestion, index) => (
                          <button
                            key={`${suggestion.title || ""}-${suggestion.artist || ""}-${index}`}
                            type="button"
                            onClick={() => applyImportEnrichSuggestion(suggestion)}
                            className="rounded-2xl border border-[#5a3824]/12 bg-white/70 px-4 py-3 text-left transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:hover:bg-white/10"
                          >
                            <span className="block text-sm font-black text-[#241a16] theme-dark:text-white">
                              {suggestion.title || importPreview.title}
                            </span>

                            <span className="mt-1 block text-xs font-semibold text-[#6b4229] theme-dark:text-zinc-400">
                              {[
                                suggestion.artist,
                                suggestion.year,
                              ]
                                .filter(Boolean)
                                .join(" · ") || tr("common.noAdditionalInfo", "Ei lisätietoja")}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isEditing && (
              <div className="order-2 rounded-[1.5rem] border border-[#5a3824]/10 bg-white/45 p-4 theme-dark:border-white/10 theme-dark:bg-white/[0.03]">
                <button
                  type="button"
                  onClick={() => setIsBulkInputOpen((current) => !current)}
                  className={[
                    "flex w-full items-center justify-between gap-3 rounded-2xl border px-5 py-4 text-left transition",
                    isBulkInputOpen
                      ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15"
                      : "border-[#5a3824]/15 bg-white/70 text-[#5a3824] hover:bg-white theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-zinc-200 theme-dark:hover:bg-white/10",
                  ].join(" ")}
                >
                  <span>
                    <span className="block text-sm font-black">
                      {isBulkInputOpen
                        ? tr("manualAdd.close", "Sulje käsin lisäys")
                        : tr("manualAdd.open", "3. Lisää biisejä käsin")}
                    </span>

                    <span className="mt-1 block text-xs font-semibold opacity-70">
                      {tr("manualAdd.onePerLine", "Kirjoita yksi biisi per rivi")}
                    </span>
                  </span>

                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-current/20 text-lg font-black">
                    {isBulkInputOpen ? "×" : "+"}
                  </span>
                </button>

                {isBulkInputOpen && (
                  <label className="mt-4 grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
                    {tr("manualAdd.songs", "Biisit")}
                    <textarea
                      value={bulkSongsText}
                      onChange={(event) => setBulkSongsText(event.target.value)}
                      rows={6}
                      placeholder={"Aamu - Pepe Willberg\nAutiotalo - Dingo\nBasket Case - Green Day"}
                      className="resize-y rounded-2xl border border-[#5a3824]/15 bg-white/80 px-4 py-3 leading-7 outline-none theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white"
                    />

                    <span className="text-xs font-normal leading-6 text-[#6b4229] theme-dark:text-zinc-400">
                      {tr("manualAdd.help", "Kirjoita yksi tai useampi biisi. Jokainen rivi tallennetaan omaksi biisiksi. Muoto: Biisin nimi - Artisti.")}
                    </span>
                  </label>
                )}
              </div>
            )}

            {(isEditing || isBulkInputOpen || importPreview) && (
              <button
                type="submit"
                className="order-6 inline-flex justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca]"
            >
              {isEditing
                ? tr("actions.saveChanges", "Tallenna muutokset")
                : tr("actions.saveAddedSongs", "Tallenna lisätyt biisit kirjastoon")}
              </button>
            )}
          </form>
        </div>
      </section>

      <section className="min-w-0 max-w-full overflow-hidden rounded-none border-y border-[#5a3824]/12 bg-[#fff8ef]/80 px-3 py-6 shadow-none theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] lg:rounded-[2rem] lg:border lg:p-8 lg:shadow-[0_18px_50px_rgba(58,43,24,0.10)]">

      <div className="grid gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
            {tr("library.eyebrow", "Kaikki biisit")}
          </p>

          <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white">
            {tr(
              "library.songCount",
              "{{count}} biisiä kirjastossa",
              { count: songs.length }
            )}
          </h2>
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tr("filters.searchPlaceholder", "Hae biisiä, artistia tai genreä...")}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold outline-none theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white"
          />

          <select
            value={artistFilter}
            onChange={(event) => setArtistFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
            <option value="all">{tr("filters.allArtists", "Kaikki artistit")}</option>
            {artistOptions.map((artist) => (
              <option key={artist} value={artist}>
                {artist}
              </option>
            ))}
          </select>

          <select
            value={genreFilter}
            onChange={(event) => setGenreFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
            <option value="all">{tr("filters.allGenres", "Kaikki genret")}</option>
            {genreOptions.map((genre) => (
              <option key={genre} value={genre}>
                {getLocalizedGenreLabel(genre)}
              </option>
            ))}
          </select>

          <select
            value={decadeFilter}
            onChange={(event) => setDecadeFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
            <option value="all">{tr("filters.allDecades", "Kaikki vuosikymmenet")}</option>
            {decadeOptions.map((decade) => (
              <option key={decade} value={decade}>
                {getLocalizedDecadeLabel(decade)}
              </option>
            ))}
          </select>

          <select
            value={tempoFilter}
            onChange={(event) => setTempoFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
            <option value="all">{tr("filters.allTempos", "Kaikki tempot")}</option>
            <option value="">{tr("filters.tempoMissing", "Tempo puuttuu")}</option>
            <option value="calm">{tr("tempo.calm", "Rauhallinen")}</option>
            <option value="medium">{tr("tempo.medium", "Keskitempo")}</option>
            <option value="upbeat">{tr("tempo.upbeat", "Menevä")}</option>
          </select>

          <select
            value={languageFilter}
            onChange={(event) => setLanguageFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
            <option value="all">
              {tr("filters.allLanguagesCount", "Kaikki kielet ({{count}})", {
                count: languageCounts.all || 0,
              })}
            </option>

            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {getLocalizedLanguageLabel(
                  option.value,
                  option.value === "unknown"
                )} ({languageCounts[option.value] || 0})
              </option>
            ))}
          </select>

          <select
            value={requestableFilter}
            onChange={(event) => setRequestableFilter(event.target.value)}
            className="w-full rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-3 text-sm font-semibold text-[#241a16] outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-black/20 theme-dark:text-white theme-dark:[color-scheme:dark]"
          >
          <option value="all">{tr("filters.showAll", "Näytä kaikki")}</option>
          <option value="requestable">{tr("filters.onlyRequestable", "Vain toivottavissa")}</option>
          <option value="hidden">{tr("filters.onlyHidden", "Vain piilotetut")}</option>
          </select>

          <div className="flex flex-nowrap justify-start gap-2">
            <button
              type="button"
              onClick={() => setSortDirection("asc")}
              className={[
                "min-w-[4.5rem] rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.12em] transition",
                sortDirection === "asc"
                  ? "border-[#5a3824] bg-[#5a3824] text-white theme-dark:border-[#ef4444]/45 theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]"
                  : "border-[#5a3824]/15 bg-white/70 text-[#5a3824] hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10",
              ].join(" ")}
            >
              A–Z
            </button>

            <button
              type="button"
              onClick={() => setSortDirection("desc")}
              className={[
                "min-w-[4.5rem] rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.12em] transition",
                sortDirection === "desc"
                  ? "border-[#5a3824] bg-[#5a3824] text-white theme-dark:border-[#ef4444]/45 theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]"
                  : "border-[#5a3824]/15 bg-white/70 text-[#5a3824] hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10",
              ].join(" ")}
            >
              Z–A
            </button>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between lg:col-span-3">
            <p className="text-sm font-semibold text-[#6b4229] theme-dark:text-zinc-300">
              {tr(
                "library.requestableCount",
                "Yleisölle näkyvissä {{visible}} / {{total}} biisiä",
                {
                  visible: requestableSongCount,
                  total: songs.length,
                }
              )}
            </p>

            <button
              type="button"
              onClick={() => {
                setQuery("");
                setArtistFilter("all");
                setGenreFilter("all");
                setDecadeFilter("all");
                setTempoFilter("all");
                setLanguageFilter("all");
                setRequestableFilter("all");
                setSelectedSongIds([]);
              }}
              className="w-fit rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
            >
              {tr("filters.clear", "Tyhjennä rajaukset")}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
        <div className="grid gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-[#6b4229] theme-dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={areAllSongsSelected}
                    onChange={toggleAllSongSelections}
                    disabled={!songs.length}
                    className="h-4 w-4 accent-[#ef4444]"
                  />

                  <span>
                    {tr(
                      "selection.selectWholeLibrary",
                      "Valitse koko kirjasto ({{count}})",
                      { count: songs.length }
                    )}
                  </span>
                </label>

                <label
                  className={[
                    "flex items-center gap-3 text-sm font-semibold",
                    hasBulkSelectionFilter && filteredSongs.length
                      ? "cursor-pointer text-[#6b4229] theme-dark:text-zinc-200"
                      : "cursor-not-allowed text-zinc-400 opacity-50 theme-dark:text-zinc-600",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={areAllFilteredSongsSelected}
                    onChange={toggleAllFilteredSongSelections}
                    disabled={!filteredSongs.length || !hasBulkSelectionFilter}
                    className="h-4 w-4 accent-[#ef4444]"
                  />

                  <span>
                    {tr("selection.selectResults", "Valitse hakutulokset")}
                    {hasBulkSelectionFilter && (
                      <span className="ml-1 text-xs font-normal opacity-70">
                        ({filteredSongs.length})
                      </span>
                    )}
                  </span>
                </label>

                {selectedSongIds.length > 0 && (
                  <span className="w-fit rounded-full bg-[#ef4444]/10 px-2.5 py-1 text-xs font-black text-[#dc2626] theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]">
                    {tr("selection.selectedCount", "{{count}} valittu", {
                      count: selectedSongIds.length,
                    })}
                  </span>
                )}
              </div>

              {selectedSongIds.length > 0 && (
                <button
                  type="button"
                  onClick={clearSongSelections}
                  className="h-10 rounded-xl border border-white/10 bg-transparent px-4 text-sm font-semibold text-zinc-400 transition hover:bg-white/[0.06] hover:text-white"
                >
                  {tr("selection.clearSelected", "Peru valitut")}
                </button>
              )}
            </div>
          </div>

          {selectedSongIds.length === 1 && (
            <p className="rounded-2xl border border-[#5a3824]/10 bg-white/50 px-4 py-3 text-xs font-semibold leading-5 text-[#6b4229] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-400">
              {tr(
                "selection.singleSelectedInfo",
                "Yksi biisi valittu. Massamuokkaus avautuu vasta, kun valitset vähintään kaksi biisiä. Yksittäistä biisiä muokataan biisin omasta Muokkaa-painikkeesta."
              )}
            </p>
          )}
  
            {selectedSongIds.length > 1 && (
            <>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 xl:items-center">
              <select
                value={bulkEditLanguage}
                onChange={(event) => setBulkEditLanguage(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_NO_CHANGE}>
                  {tr("bulkEdit.languageNoChange", "Kieli: ei muutosta")}
                </option>
                {LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {getLocalizedLanguageLabel(
                  option.value,
                  option.value === "unknown"
                )}
                  </option>
                ))}
              </select>

              <select
                value={bulkEditGenre}
                onChange={(event) => setBulkEditGenre(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_NO_CHANGE}>
                  {tr("bulkEdit.genreNoChange", "Genre: ei muutosta")}
                </option>
                {genreOptions.map((genre) => (
                  <option key={genre} value={genre}>
                    {getLocalizedGenreLabel(genre)}
                  </option>
                ))}
              </select>

              <select
                value={bulkEditDecade}
                onChange={(event) => setBulkEditDecade(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_NO_CHANGE}>
                  {tr("bulkEdit.decadeNoChange", "Vuosikymmen: ei muutosta")}
                </option>
                <option value={BULK_CLEAR_VALUE}>
                  {tr("bulkEdit.clearDecade", "Poista vuosikymmen")}
                </option>
                {decadeOptions.map((decade) => (
                  <option key={decade} value={decade}>
                    {getLocalizedDecadeLabel(decade)}
                  </option>
                ))}
              </select>
             <select
                value={bulkEditTempo}
                onChange={(event) => setBulkEditTempo(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_NO_CHANGE}>
                  {tr("bulkEdit.tempoNoChange", "Tempo: ei muutosta")}
                </option>
                <option value={BULK_CLEAR_VALUE}>
                  {tr("bulkEdit.clearTempo", "Poista tempo")}
                </option>
                <option value="calm">{tr("tempo.calm", "Rauhallinen")}</option>
                <option value="medium">{tr("tempo.medium", "Keskitempo")}</option>
                <option value="upbeat">{tr("tempo.upbeat", "Menevä")}</option>
              </select>
            </div>

            <div className="grid gap-2 md:grid-cols-3 md:items-center">
              <select
                value={bulkEditTagAction}
                onChange={(event) => setBulkEditTagAction(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_TAG_NO_CHANGE}>
                  {tr("bulkEdit.tagNoChange", "Tilaisuustägi: ei muutosta")}
                </option>
                <option value={BULK_TAG_ADD}>
                  {tr("bulkEdit.addSelectedTag", "Lisää valittu tilaisuustägi")}
                </option>
                <option value={BULK_TAG_REMOVE}>
                  {tr("bulkEdit.removeSelectedTag", "Poista valittu tilaisuustägi")}
                </option>
              </select>
              <select
                value={bulkOccasionTag}
                onChange={(event) => setBulkOccasionTag(event.target.value)}
                disabled={bulkEditTagAction === BULK_TAG_NO_CHANGE}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                {localizedOccasionOptions.map((tag) => (
                  <option key={tag} value={tag}>
                    {getLocalizedOccasionTagLabel(tag)}
                  </option>
                ))}
              </select>

              <select
                value={bulkEditRequestableMode}
                onChange={(event) => setBulkEditRequestableMode(event.target.value)}
                className="h-11 rounded-xl border border-white/10 bg-black/25 px-3 text-sm font-semibold text-white outline-none"
              >
                <option value={BULK_REQUESTABLE_NO_CHANGE}>
                  {tr("bulkEdit.requestableNoChange", "Biisitoiveissa: ei muutosta")}
                </option>
                <option value={BULK_REQUESTABLE_TRUE}>
                  {tr("bulkEdit.showInRequests", "Näytä toiveissa")}
                </option>
                <option value={BULK_REQUESTABLE_FALSE}>
                  {tr("bulkEdit.hideFromRequests", "Piilota toiveista")}
                </option>
              </select>
            </div>

              <div className="grid gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
                <button
                  type="button"
                  onClick={saveBulkChangesForSelectedSongs}
                  disabled={!selectedSongIds.length || isBulkUpdating}
                  className="h-11 rounded-xl border border-emerald-400/35 bg-emerald-500/15 px-4 text-sm font-black text-emerald-100 transition hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBulkUpdating
                    ? tr("actions.saving", "Tallennetaan...")
                    : tr(
                        "bulkEdit.saveSelected",
                        "Tallenna muutokset valittuihin biiseihin ({{count}})",
                        { count: selectedSongIds.length }
                      )}
                </button>

                <button
                  type="button"
                  onClick={openBulkDeleteConfirm}
                  disabled={!selectedSongIds.length || isBulkUpdating}
                  className="h-11 rounded-xl border border-[#ef4444]/45 bg-[#ef4444] px-4 text-sm font-black text-white shadow-lg shadow-[#ef4444]/15 transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {tr("bulkEdit.deleteSelected", "Poista valitut")}
                </button>
              </div>

              <p className="text-xs leading-5 text-zinc-500">
                {tr("bulkEdit.help", "Valitse ensin mitä kenttiä muutetaan. Tägi muuttuu vain, jos valitset “Lisää valittu tilaisuustägi” tai “Poista valittu tilaisuustägi”. Lopuksi paina tallennusta.")}
              </p>
            </>
          )}
        </div>
      </div>

        <div className="mt-6 grid gap-3">
          {visibleSongs.map((song) => (
            <article
              key={song.id}
              className={[
                "w-full rounded-none border-x-0 border-y px-3 py-3 transition lg:rounded-[1.5rem] lg:border lg:p-4",
                editingSongId === song.id
                  ? "border-[#5a3824]/35 bg-[#fff8ef] shadow-[0_14px_36px_rgba(58,43,24,0.12)] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10"
                  : "border-[#5a3824]/12 bg-white/70 theme-dark:border-white/10 theme-dark:bg-white/[0.04]",
              ].join(" ")}
            >
              <div className="grid min-w-0 gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
                <label className="flex items-center gap-2 text-xs font-black text-[#5a3824] theme-dark:text-zinc-300">
                  <input
                    type="checkbox"
                    checked={selectedSongIdSet.has(String(song.id))}
                    onChange={() => toggleSongSelection(song.id)}
                    className="h-5 w-5 accent-[#5a3824] theme-dark:accent-[#ef4444]"
                  />

                  <span className="sm:hidden">{tr("actions.select", "Valitse")}</span>
                </label>

                <div className="min-w-0">
                  <h3 className="break-words text-sm font-semibold leading-tight text-[#241a16] theme-dark:text-white sm:text-lg">
                    {song.title}
                  </h3>

                  <p className="mt-0.5 text-[0.7rem] leading-5 text-[#6b4229] theme-dark:text-zinc-400 sm:mt-1 sm:text-sm sm:leading-6">
                    {[
                      song.artist,
                      getLocalizedDecadeLabel(getSongDecade(song)),
                      getLocalizedGenreLabel(song.genre),
                      shouldShowLanguagePill(getSongLanguage(song))
                        ? getLocalizedLanguageLabel(getSongLanguage(song))
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" · ") || tr("common.noAdditionalInfo", "Ei lisätietoja")}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span
                      className={[
                        "inline-flex rounded-full border px-2.5 py-1 text-[0.68rem] font-semibold",
                        getSongIsRequestable(song)
                          ? "border-red-200 bg-red-50 text-red-700 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                          : "border-red-200 bg-red-50 text-red-700 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200",
                      ].join(" ")}
                    >
                      {getSongIsRequestable(song)
                        ? tr("requestability.requestable", "Toivottavissa")
                        : tr("requestability.hidden", "Piilotettu toiveista")}
                    </span>

                    {getLocalizedTempoLabel(song.tempo) && (
                      <span className="inline-flex rounded-full border border-amber-300/40 bg-amber-400/10 px-2.5 py-1 text-[0.68rem] font-semibold text-amber-700 theme-dark:border-amber-300/25 theme-dark:text-amber-200">
                        {getLocalizedTempoLabel(song.tempo)}
                      </span>
                    )}

                      {hasChordProNotes(song) &&
                        (canUseChordLyrics ? (
                          <span
                            className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[0.68rem] font-black text-emerald-700 theme-dark:border-emerald-400/30 theme-dark:bg-emerald-500/10 theme-dark:text-emerald-200"
                            aria-label={tr(
                              "lyrics.availableAria",
                              "Biisillä {{title}} on sanat ja soinnut",
                              { title: song.title }
                            )}
                            title={tr("lyrics.availableTitle", "Biisillä on sanat ja soinnut")}
                          >
                            ♫ {tr("lyrics.shortLabel", "Sanat & soinnut")}
                          </span>
                        ) : (
                        <span
                          className="inline-flex cursor-not-allowed items-center rounded-full border border-zinc-300 bg-zinc-100 px-2.5 py-1 text-[0.68rem] font-black text-zinc-500 theme-dark:border-white/15 theme-dark:bg-white/[0.06] theme-dark:text-zinc-400"
                          aria-label={tr(
                            "lyrics.plusAria",
                            "Sanat ja soinnut ovat käytettävissä Plus-paketissa"
                          )}
                          title={tr(
                            "lyrics.plusTitle",
                            "Sanat ja soinnut kuuluvat Plus-pakettiin"
                          )}
                        >
                          🔒 {tr("lyrics.shortPlusLabel", "Sanat & soinnut · Plus")}
                        </span>
                      ))}
                  </div>

                  {parseOccasionTags(getSongOccasionTags(song)).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {parseOccasionTags(getSongOccasionTags(song)).map((tag) => (
                        <span
                          key={tag}
                          className={[
                            "inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.08em]",
                            getOccasionTagClass(tag),
                          ].join(" ")}
                        >
                          {getLocalizedOccasionTagLabel(tag)}
                        </span>
                      ))}

                    </div>
                  )}
                </div>

                <div className="order-5 flex flex-wrap gap-1.5 border-t border-[#5a3824]/10 pt-3 theme-dark:border-white/10 sm:border-t-0 sm:pt-0">
                  <button
                    type="button"
                    onClick={() => toggleSongRequestable(song)}
                    className={[
                      "rounded-full border px-2.5 py-1 text-[0.7rem] font-semibold transition sm:px-4 sm:py-2 sm:text-sm",
                      getSongIsRequestable(song)
                        ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200"
                        : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200",
                    ].join(" ")}
                  >
                    {getSongIsRequestable(song)
                      ? tr("actions.hide", "Piilota")
                      : tr("actions.restore", "Palauta")}
                  </button>

                  <button
                    type="button"
                    onClick={() => startEdit(song)}
                    className="rounded-full border border-[#5a3824]/15 px-2.5 py-1 text-[0.7rem] font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:px-4 sm:py-2 sm:text-sm"
                  >
                    {tr("actions.edit", "Muokkaa")}
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeleteConfirmSong(song)}
                    className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[0.7rem] font-semibold text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200 sm:px-4 sm:py-2 sm:text-sm"
                  >
                    {tr("actions.delete", "Poista")}
                  </button>
                </div>
              </div>
            </article>
          ))}
          {filteredSongs.length > visibleSongs.length && (
            <button
              type="button"
              onClick={() => setVisibleSongCount((current) => current + 80)}
              className="mx-auto mt-4 rounded-full border border-[#5a3824]/15 bg-white/80 px-5 py-2.5 text-sm font-black text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-200 theme-dark:hover:bg-white/10"
            >
              {tr(
                "library.showMore",
                "Näytä lisää biisejä ({{visible}}/{{total}})",
                {
                  visible: visibleSongs.length,
                  total: filteredSongs.length,
                }
              )}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
/*
 * Admin-pyyntöjen autentikointi perustuu palvelimen HttpOnly-session-cookieen.
 * Lähetetään cookie mukana myös localhostin frontend -> backend -pyynnöissä.
 */
function fetchWithCredentials(input, init = {}) {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}


